import React, { useState, useEffect } from 'react';
import api from './api';
import {
  couleurs, espace, rayon, texte, police, carte as carteStyle,
  champ as champStyle, bouton, badge as badgeStyle, bandeau, tableau, pastille,
} from './theme';
import { useMobile, useDefilementBloque, useEchappe } from './ecran';

// ─── COMPOSANTS UTILITAIRES ────────────────────────────────

function Section({ titre, children, onAjouter }) {
  return (
    <div style={s.section}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={s.sectionTitre}>{titre}</h2>
        {onAjouter && (
          <button style={s.btnPlus} onClick={onAjouter} title="Ajouter">＋</button>
        )}
      </div>
      {children}
    </div>
  );
}

/* Badge posé à la suite d'un libellé (d'où la marge à gauche).
   `ton` : succes | alerte | danger | accent | neutre. Passer par cette
   fonction plutôt que par une paire de codes hexadécimaux garantit que
   « en attente » a la même couleur partout dans l'outil. */
const badge = (ton = 'neutre', extra = {}) =>
  ({ ...badgeStyle(ton), marginLeft: espace.sm, ...extra });

// Variante pour les identifiants (EPC, terminaux) : même badge, en
// chasse fixe, pour pouvoir les comparer d'une ligne à l'autre.
const badgeMono = (ton = 'neutre') => badge(ton, { fontFamily: police.mono, fontWeight: 500 });

function InfoBulle({ texte }) {
  const [ouvert, setOuvert] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOuvert(o => !o)}
        style={s.iconInfo}
        aria-label="Plus d'informations"
      >
        i
      </button>
      {ouvert && (
        <div style={s.bulleInfo}>
          <button type="button" onClick={() => setOuvert(false)} style={s.bulleInfoFermer} aria-label="Fermer">✕</button>
          <p style={{ margin: 0, paddingRight: 16 }}>{texte}</p>
        </div>
      )}
    </span>
  );
}

// Affiche la vraie valeur EN VIGUEUR d'un réglage partenaire (celle que
// resolu.* contient réellement, qu'elle vienne de la base ou du .env de
// secours) — jamais un champ vide qui laisse deviner. C'est la valeur à
// copier telle quelle dans la config du partenaire pour être sûr que les
// deux côtés matchent, sans avoir à ouvrir un .env ou une base à la main.
// Masquée par défaut pour les secrets (masque=true), un clic sur "afficher"
// la révèle en clair — elle est de toute façon déjà présente côté client
// dans le formulaire d'édition juste au-dessus, ceci ne change pas ce qui
// est exposé, seulement ce qui est LISIBLE par un humain sans deviner.
function ValeurActive({ label, valeur, masque }) {
  const [visible, setVisible] = useState(!masque);
  const vide = valeur === null || valeur === undefined || valeur === '';
  const copier = () => { if (!vide) navigator.clipboard.writeText(String(valeur)); };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 0' }}>
      <span style={{ color: couleurs.encreDouce, minWidth: 210 }}>{label} :</span>
      <span style={{ fontFamily: 'monospace', color: vide ? couleurs.encreFaible : couleurs.encre }}>
        {vide ? '(vide)' : (masque && !visible ? '••••••••••••' : String(valeur))}
      </span>
      {masque && !vide && (
        <button type="button" onClick={() => setVisible(v => !v)} style={s.idCopier}>
          {visible ? 'masquer' : 'afficher'}
        </button>
      )}
      {!vide && (
        <button type="button" onClick={copier} style={s.idCopier}>copier</button>
      )}
    </div>
  );
}

/* `autoComplete` par défaut à « new-password » sur les champs de mot de
   passe — ajouté le 2026-08-27, et c'est une correction de sécurité,
   pas de confort.

   Le navigateur remplissait tout seul le mot de passe des fenêtres de
   confirmation. Or ce mot de passe EST la protection : il sert à
   prouver que la personne devant l'écran est bien titulaire du compte,
   et non quelqu'un qui trouve une session ouverte sur un poste partagé.
   Pré-rempli, il ne prouve plus rien — il ne reste qu'à cliquer.

   « new-password » et non « off » : les gestionnaires de mots de passe
   ignorent « off » sur un champ de type password depuis des années,
   alors qu'ils respectent « new-password », qui signifie « ceci n'est
   pas l'identifiant enregistré pour ce site ». */
function Champ({ label, value, onChange, placeholder, type = 'text', info, autoComplete,
                 liste }) {
  return (
    <div style={s.champ}>
      <label style={s.label}>
        {label}
        {info && <InfoBulle texte={info} />}
      </label>
      <input style={s.input} type={type} value={value}
        onChange={e => onChange(e.target.value)} placeholder={placeholder}
        // `liste` : id d'un <datalist> voisin — suggestions sans imposer,
        // la saisie reste libre.
        list={liste}
        autoComplete={autoComplete || (type === 'password' ? 'new-password' : 'off')}
        // Gestionnaires tiers (LastPass, 1Password) : ils ne lisent pas
        // autocomplete, mais respectent ces marqueurs.
        data-lpignore="true" data-1p-ignore="true" />
    </div>
  );
}

/* Les appels historiques passent une couleur (`couleur="#8b5cf6"`) —
   on ne la lit plus. Le rôle du bouton se déduit de son emploi, et
   trois variantes suffisent (voir bouton() dans theme.js). Le
   paramètre est conservé pour ne pas avoir à toucher les ~40 appels :
   `variante` permet de préciser quand ça compte. */
function Bouton({ label, onClick, variante = 'primaire', disabled = false }) {
  return (
    <button
      style={{
        ...bouton(variante),
        ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
      }}
      onClick={onClick} disabled={disabled}>{label}</button>
  );
}

function BoutonSm({ label, onClick, variante = 'discret' }) {
  return <button style={bouton(variante, 'sm')} onClick={onClick}>{label}</button>;
}

/* Suppression en deux temps. Le premier bouton est neutre — il ne
   détruit rien, il ne fait qu'ouvrir la question ; seule la
   CONFIRMATION est rouge. Un bouton rouge qui ne détruit encore rien
   use l'alerte pour rien. */
function BoutonSupprimer({ onClick }) {
  const [confirme, setConfirme] = useState(false);
  if (confirme) return (
    <div style={{ display: 'flex', gap: espace.xs }}>
      <button style={{ ...bouton('danger', 'sm'), background: couleurs.danger, color: couleurs.surAccent }}
        onClick={onClick}>Confirmer</button>
      <button style={bouton('discret', 'sm')} onClick={() => setConfirme(false)}>Annuler</button>
    </div>
  );
  return <button style={bouton('discret', 'sm')} onClick={() => setConfirme(true)}>Supprimer</button>;
}

function BoutonAnnulerCommande({ onClick }) {
  const [confirme, setConfirme] = useState(false);
  if (confirme) return (
    <div style={{ display: 'flex', gap: espace.xs }}>
      <button style={{ ...bouton('danger', 'sm'), background: couleurs.danger, color: couleurs.surAccent }}
        onClick={onClick}>Confirmer l'annulation</button>
      <button style={bouton('discret', 'sm')} onClick={() => setConfirme(false)}>Retour</button>
    </div>
  );
  return <button style={bouton('discret', 'sm')} onClick={() => setConfirme(true)}>Annuler la commande</button>;
}

/* `role` : un succès est annoncé poliment (`status`), une erreur
   interrompt (`alert`). Sans cela le message n'existait que pour qui
   distingue le vert du rouge. */
function Message({ texte, type }) {
  if (!texte) return null;
  return (
    <p style={bandeau(type === 'succes' ? 'succes' : 'danger')}
       role={type === 'succes' ? 'status' : 'alert'}>{texte}</p>
  );
}

function Modal({ titre, onClose, children }) {
  return (
    <div className="jums-modale-voile" style={s.overlay}>
      <div className="jums-modale" style={s.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 'bold', color: couleurs.encre }}>{titre}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: couleurs.encreDouce }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── Le choix de section sur écran étroit (03/09/2026) ─────────────
   L'administration compte neuf sections. Sur ordinateur elles tiennent
   sur un rail souligné ; sur un téléphone de 360 px, le rail se replie
   sur trois lignes de pastilles minuscules et on ne sait plus où l'on
   est.

   À la place : un bouton pleine largeur qui affiche la section
   courante, et qui ouvre une feuille listant les neuf. C'est la même
   liste, dans le même ordre, avec les mêmes mots — seule la forme
   change. La section active y est marquée du même trait d'accent que
   sur le rail, pour qu'on retrouve la grammaire de la console.

   Rien de ceci n'est monté au-dessus de 760 px. */
function SelecteurSection({ onglets, onglet, setOnglet }) {
  const [ouvert, setOuvert] = useState(false);
  useDefilementBloque(ouvert);
  useEchappe(ouvert, () => setOuvert(false));

  const courant = onglets.find(o => o.id === onglet);

  return (
    <>
      {/* Le sélecteur reste accroché sous la barre de navigation pendant
          qu'on fait défiler : sur un écran long — la liste des gobelets,
          le journal des rejets — on perd sinon de vue la section où l'on
          se trouve, et il faut remonter tout en haut rien que pour en
          changer.

          L'enveloppe porte le fond de la page et déborde jusqu'aux bords
          (voir `.jums-collant` dans mobile.css) : sans ce débord, le
          contenu défilerait dans les quelques millimètres de marge
          laissés de chaque côté. */}
      <div className="jums-collant">
        <button onClick={() => setOuvert(true)}
          aria-haspopup="menu" aria-expanded={ouvert}
          style={sel.declencheur}>
          <span style={sel.declencheurGauche}>
            <span style={texte.etiquette}>Section</span>
            <span style={sel.declencheurNom}>{courant ? courant.label : '—'}</span>
          </span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round"
               strokeLinejoin="round" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>

      {ouvert && (
        <>
          <div style={sel.voile} onClick={() => setOuvert(false)} aria-hidden="true" />
          <div style={sel.feuille} role="menu" aria-label="Sections de l'administration">
            <div style={sel.poignee} aria-hidden="true" />
            {onglets.map(o => (
              <button key={o.id} role="menuitem" disabled={o.disabled}
                aria-current={onglet === o.id ? 'true' : undefined}
                onClick={() => { if (!o.disabled) { setOnglet(o.id); setOuvert(false); } }}
                style={{
                  ...sel.entree,
                  ...(onglet === o.id ? sel.entreeActive : {}),
                  opacity: o.disabled ? 0.4 : 1,
                  cursor: o.disabled ? 'not-allowed' : 'pointer',
                }}>
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* Styles du sélecteur — tous empruntés au thème, aucun ton nouveau. */
const sel = {
  declencheur: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    width: '100%', gap: espace.md,
    /* Plus de marge basse ici : c'est l'enveloppe collante qui porte
       l'espace sous le bouton, sinon le contenu défilerait dans cette
       marge, sous un bouton qui semble flotter. */
    padding: `10px ${espace.md}px`,
    background: couleurs.surface, color: couleurs.encre,
    border: `1px solid ${couleurs.bordFort}`, borderRadius: rayon.md,
    cursor: 'pointer', fontFamily: police.ui, textAlign: 'left',
  },
  declencheurGauche: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 },
  declencheurNom: { fontSize: 15, fontWeight: 600, color: couleurs.encre },
  voile: {
    position: 'fixed', inset: 0, background: couleurs.voile, zIndex: 1400,
  },
  /* Une feuille qui monte du bas plutôt qu'une fenêtre centrée : le
     pouce atteint le bas de l'écran, jamais le milieu. */
  feuille: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1401,
    maxHeight: '78vh', overflowY: 'auto',
    background: couleurs.surface,
    borderTop: `1px solid ${couleurs.bord}`,
    borderTopLeftRadius: rayon.xl, borderTopRightRadius: rayon.xl,
    padding: `${espace.sm}px 0 calc(${espace.md}px + env(safe-area-inset-bottom))`,
  },
  poignee: {
    width: 36, height: 4, borderRadius: rayon.rond,
    background: couleurs.bordFort, margin: `${espace.sm}px auto ${espace.md}px`,
  },
  entree: {
    display: 'block', width: '100%', textAlign: 'left',
    background: 'transparent', border: 'none',
    borderLeft: '3px solid transparent',
    padding: `13px ${espace.lg}px`, fontSize: 15,
    fontFamily: police.ui, color: couleurs.encreDouce,
  },
  entreeActive: {
    color: couleurs.encre, fontWeight: 600,
    borderLeftColor: couleurs.accent, background: couleurs.surfaceDouce,
  },
};

function Liste({ items, vide, renderItem }) {
  if (!items || items.length === 0) return <p style={s.vide}>{vide}</p>;
  return (
    <div style={s.liste}>
      {items.map((item, i) => (
        <div key={item.id ?? i} style={s.listeItem}>
          {renderItem(item)}
        </div>
      ))}
    </div>
  );
}


// ─── FESTIVALS ─────────────────────────────────────────────

/* Les deux actions qui détruisent des données sans laisser de trace :
   réinitialiser un festival, et le supprimer. Toutes les autres
   suppressions de l'outil (un bar, un gobelet) se rattrapent en
   ressaisissant ; celles-ci non.

   Trois barrières, chacune contre une erreur différente :
     • le mot de passe  → prouve que c'est bien vous (une session admin
       reste souvent ouverte sur un poste partagé) ;
     • le nom du festival à retaper → prouve que vous visez bien CE
       festival-là. C'est l'erreur qui arrive vraiment : on ne se trompe
       pas de personne, on se trompe de ligne dans une liste ;
     • une sauvegarde prise automatiquement juste avant, côté serveur.

   Le bouton reste inerte tant que les deux champs ne sont pas remplis :
   pas de clic réflexe possible. */
export function ModalActionDestructrice({ action, festival, onClose, onFait }) {
  const [motDePasse, setMotDePasse] = useState('');
  const [nomTape, setNomTape]       = useState('');
  const [motif, setMotif]           = useState('');
  const [enCours, setEnCours]       = useState(false);
  const [msg, setMsg]               = useState({ texte: '', type: '' });

  const suppression = action === 'suppression';
  const nomCorrect  = nomTape.trim().toLowerCase() === festival.nom.trim().toLowerCase();
  const pret        = motDePasse.length > 0 && nomCorrect && !enCours;

  const executer = async () => {
    if (!pret) return;
    setEnCours(true); setMsg({ texte: '', type: '' });
    const corps = { mot_de_passe: motDePasse, nom_festival: nomTape, motif: motif || null };
    try {
      // axios exige `data` pour joindre un corps à un DELETE.
      const res = suppression
        ? await api.delete(`/admin/festivals/${festival.id}`, { data: corps })
        : await api.post(`/admin/reinitialiser/${festival.id}`, corps);
      onFait(res.data, suppression);
      onClose();
    } catch (e) {
      setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' });
      setEnCours(false);
    }
  };

  return (
    <Modal
      titre={suppression
        ? `Supprimer définitivement « ${festival.nom} »`
        : `Réinitialiser « ${festival.nom} »`}
      onClose={onClose}>
      <div style={s.formulaire}>
        <div style={{ ...bandeau('danger'), margin: 0 }}>
          {suppression ? (
            <>
              <p style={{ margin: `0 0 ${espace.sm}px` }}>
                <strong>Tout le festival disparaît, définitivement.</strong>
              </p>
              <p style={{ margin: 0 }}>
                Ses bars, ses lecteurs, ses bacs, ses produits, <strong>la liste de
                ses gobelets encodés</strong>, ses bracelets de barman, ses ventes,
                ses transactions et sa configuration partenaire. Il faudra tout
                réencoder pour repartir.
              </p>
            </>
          ) : (
            <>
              <p style={{ margin: `0 0 ${espace.sm}px` }}>
                <strong>Le festival repart à zéro.</strong>
              </p>
              <p style={{ margin: 0 }}>
                Les commandes en cours sont annulées, tous les gobelets
                repassent en stock, les ventes et les remboursements sont
                effacés, les compteurs de bacs remis à zéro. La
                configuration (bars, lecteurs, gobelets encodés, bracelets)
                n'est pas touchée.
              </p>
            </>
          )}
        </div>

        <p style={{ ...texte.aide, margin: 0 }}>
          Une sauvegarde de la base est prise automatiquement juste avant —
          vous la retrouverez dans l'onglet <strong>Sauvegardes</strong>, à son nom.
        </p>

        <Champ
          label={`Tapez le nom du festival pour confirmer : ${festival.nom}`}
          value={nomTape} onChange={setNomTape} placeholder={festival.nom} />

        <Champ label="Mot de passe de votre compte *" value={motDePasse} type="password"
          onChange={setMotDePasse} placeholder="Le même qu'à la connexion" />

        <Champ label="Motif (facultatif — conservé dans les journaux du serveur)"
          value={motif} onChange={setMotif}
          placeholder={suppression ? 'Ex: festival de test' : 'Ex: fin de la journée de test'} />

        <Bouton
          label={enCours
            ? 'Sauvegarde en cours, puis exécution…'
            : (suppression ? 'Supprimer définitivement ce festival' : 'Réinitialiser ce festival')}
          onClick={executer} variante="danger" disabled={!pret} />
        <Message texte={msg.texte} type={msg.type} />
      </div>
    </Modal>
  );
}

/* Formulaire de création / modification d'un festival.

   Exporté parce qu'il sert à DEUX endroits : ici, dans l'onglet
   « Festival », pour modifier celui qui est actif ; et sur l'écran de
   choix du festival (App.js), pour en créer un sans avoir à entrer
   d'abord dans un festival existant — ce qui était impossible tant
   qu'aucun n'était sélectionné. */
export function ModalFestival({ festival, onClose, onEnregistre }) {
  const [nom, setNom]             = useState(festival?.nom || '');
  const [dateDebut, setDateDebut] = useState(festival?.date_debut || '');
  const [dateFin, setDateFin]     = useState(festival?.date_fin || '');
  const [lieu, setLieu]           = useState(festival?.lieu || '');
  const [partenaireEventId, setPartenaireEventId] = useState(festival?.partenaire_event_id || '');
  const [msg, setMsg]             = useState({ texte: '', type: '' });

  const enregistrer = async () => {
    if (!nom || !dateDebut || !dateFin || !lieu) {
      setMsg({ texte: 'Tous les champs marqués * sont obligatoires', type: 'erreur' });
      return;
    }
    const corps = {
      nom, date_debut: dateDebut, date_fin: dateFin, lieu,
      partenaire_event_id: partenaireEventId || null,
    };
    try {
      const res = festival
        ? await api.put(`/admin/festivals/${festival.id}`, corps)
        : await api.post('/admin/festivals', corps);
      onEnregistre(res.data, !festival);
      onClose();
    } catch (e) {
      setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' });
    }
  };

  return (
    <Modal titre={festival ? 'Modifier le festival' : 'Nouveau festival'} onClose={onClose}>
      <div style={s.formulaire}>
        <Champ label="Nom *" value={nom} onChange={setNom} placeholder="Ex: Festival Rock 2025" />
        <Champ label="Date début *" value={dateDebut} onChange={setDateDebut} type="date" />
        <Champ label="Date fin *" value={dateFin} onChange={setDateFin} type="date" />
        <Champ label="Lieu *" value={lieu} onChange={setLieu} placeholder="Ex: Paris" />
        <Champ label="ID événement partenaire" value={partenaireEventId}
          onChange={setPartenaireEventId} placeholder="Optionnel" />
        <Bouton label={festival ? 'Enregistrer les modifications' : 'Créer le festival'}
          onClick={enregistrer} />
        <Message texte={msg.texte} type={msg.type} />
      </div>
    </Modal>
  );
}


/* L'onglet « Festival » — refonte du 2026-08-27.

   Il ne montre plus la LISTE des festivals, mais uniquement celui qui
   est actif. Motif : il existait deux boutons « Changer » qui se
   ressemblaient et faisaient deux choses différentes — l'un ramenait à
   l'écran de choix, l'autre à cet onglet. Choisir un festival et
   administrer celui qu'on a choisi sont deux gestes distincts ; ils ont
   maintenant chacun leur écran.

   Choisir un festival ou en créer un : l'écran de choix (bouton
   « Changer » de la barre du haut).
   Modifier, réinitialiser ou supprimer celui qu'on administre : ici. */
function Festivals({ festival, onFestivalChange }) {
  const [modifier, setModifier] = useState(false);
  const [msg, setMsg]           = useState({ texte: '', type: '' });
  // { action: 'reinitialisation' | 'suppression' }
  const [actionDestructrice, setActionDestructrice] = useState(null);

  if (!festival) return <Section titre="Festival"><p style={s.vide}>Aucun festival actif.</p></Section>;

  /* La réponse porte le nom du fichier de sauvegarde — on l'affiche. Si
     le serveur n'a pas réussi à la prendre, l'action a quand même eu
     lieu (voir sauvegardes.py) et il faut le dire tout de suite, pas le
     laisser découvrir le jour où on en a besoin. */
  const actionFaite = (data, suppression) => {
    if (suppression) {
      // Le festival administré n'existe plus : rester sur cet écran
      // n'aurait aucun sens. On remonte l'information, et l'application
      // ramène à l'écran de choix.
      onFestivalChange(null);
      return;
    }
    setMsg(data.sauvegarde
      ? { texte: `${data.message} — sauvegarde préalable : ${data.sauvegarde}`, type: 'succes' }
      : { texte: `${data.message} — ⚠ ATTENTION : la sauvegarde préalable a ÉCHOUÉ, rien n'a été conservé`,
          type: 'erreur' });
  };

  return (
    <>
      <Section titre="Festival">
        <p style={s.hint}>
          Les réglages du festival que vous administrez.
        </p>
        <Message texte={msg.texte} type={msg.type} />

        {/* Le cadre ci-dessous a l'air cliquable sans l'être — d'où ce
            bouton explicite : c'est lui qui mène au choix du festival,
            et il fait exactement ce que fait « Changer » en haut de la
            page. Deux chemins, une seule destination. */}
        <div style={s.listeItem}>
          <div style={s.festivalItem}>
            <div>
              <strong>{festival.nom}</strong>
              <span style={s.badge}>{festival.lieu}</span>
              <span style={s.festivalDates}>{festival.date_debut} → {festival.date_fin}</span>
              <div style={s.idLigne}>
                <span style={s.idTexte}>festival_id : {festival.id}</span>
                <button
                  style={s.idCopier}
                  onClick={() => navigator.clipboard.writeText(festival.id)}
                  title="Copier l'identifiant du festival (à renseigner dans config.json du collecteur)"
                >
                  Copier
                </button>
              </div>
            </div>
            {/* Les deux actions destructrices ne sont PAS des boutons
                directs : elles ouvrent une fenêtre qui exige le mot de
                passe et le nom du festival. Un bouton rouge qui détruit
                au premier clic n'a rien à faire à côté de « Modifier ». */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Bouton label="Changer de festival" variante="primaire"
                onClick={() => onFestivalChange(null)} />
              <BoutonSm label="Modifier" onClick={() => setModifier(true)} />
              <BoutonSm label="Réinitialiser…"
                onClick={() => setActionDestructrice('reinitialisation')} />
              <BoutonSm label="Supprimer…" variante="danger"
                onClick={() => setActionDestructrice('suppression')} />
            </div>
          </div>
        </div>
      </Section>

      {modifier && (
        <ModalFestival
          festival={festival}
          onClose={() => setModifier(false)}
          onEnregistre={(maj) => {
            onFestivalChange(maj);
            setMsg({ texte: 'Festival modifié', type: 'succes' });
          }} />
      )}

      {actionDestructrice && (
        <ModalActionDestructrice
          action={actionDestructrice}
          festival={festival}
          onClose={() => setActionDestructrice(null)}
          onFait={actionFaite} />
      )}
    </>
  );
}


// ─── LECTEURS (bars, tapis et bacs réunis) ─────────────────
// Refonte du 2026-08-16, remplace les onglets « Bars & Tapis » et
// « Bacs retour ». Motif : avec une vingtaine de lecteurs répartis sur
// plusieurs bars, l'ancien écran obligeait à entrer bar par bar pour
// voir ses tapis. On ne voyait jamais l'ensemble, et une configuration
// manquante ne se repérait qu'en visitant chaque bar l'un après l'autre.
//
// Ici : une colonne par bar, plus une colonne pour les bacs — c'est la
// carte du site. Le bandeau du haut dit en une ligne ce qui reste à
// régler avant l'ouverture, sans avoir à parcourir quoi que ce soit.
//
// « Prêt » veut dire : ce lecteur peut réellement travailler.
//   tapis → un terminal partenaire ET une IP de lecteur ;
//   bac   → une IP suffit (un bac n'encaisse rien, il n'a pas de terminal).

/* ── Configuration du scan ─────────────────────────────────────────
   Deux réglages qui vivaient dans « Configuration partenaire » et qui
   n'avaient rien à y faire : ils ne décrivent pas la relation avec le
   cashless, mais le geste du barman au comptoir. Leur place est ici,
   sous les lecteurs qu'ils pilotent.

   Ils sont stockés dans la même ligne de configuration que les réglages
   partenaire. C'est pourquoi l'API n'écrit, depuis le 01/09/2026, que
   les champs qu'on lui envoie : sans ça, enregistrer ici effacerait le
   secret de signature, et enregistrer là-bas effacerait ces deux
   valeurs. */
function ConfigurationScan({ festival }) {
  const [delai, setDelai]       = useState('');
  const [fenetre, setFenetre]   = useState('');
  const [defauts, setDefauts]   = useState({});
  const [resolu, setResolu]     = useState(null);
  const [msg, setMsg]           = useState({ texte: '', type: '' });

  const charger = async () => {
    try {
      const { config, resolu, defauts_env } =
        (await api.get(`/admin/partenaire-config/${festival.id}`)).data;
      setResolu(resolu);
      setDefauts(defauts_env);
      setDelai(config.delai_scan_minutes != null ? String(config.delai_scan_minutes) : '');
      setFenetre(config.bracelet_fenetre_secondes != null
        ? String(config.bracelet_fenetre_secondes) : '');
    } catch (e) { console.error(e); }
  };
  useEffect(() => { charger(); }, [festival]);

  const sauvegarder = async () => {
    setMsg({ texte: '', type: '' });
    // Un délai à zéro rembourserait toute vente à la seconde où elle
    // arrive : le barman n'aurait jamais le temps de scanner.
    if (delai !== '' && (isNaN(parseInt(delai, 10)) || parseInt(delai, 10) < 1)) {
      setMsg({ texte: 'Le délai de scan doit valoir au moins 1 minute.', type: 'erreur' });
      return;
    }
    if (fenetre !== '' && (isNaN(parseInt(fenetre, 10)) || parseInt(fenetre, 10) < 1)) {
      setMsg({ texte: 'La fenêtre du geste doit valoir au moins 1 seconde.', type: 'erreur' });
      return;
    }
    try {
      await api.put(`/admin/partenaire-config/${festival.id}`, {
        delai_scan_minutes: delai === '' ? null : parseInt(delai, 10),
        bracelet_fenetre_secondes: fenetre === '' ? null : parseInt(fenetre, 10),
      });
      setMsg({ texte: 'Réglages enregistrés.', type: 'succes' });
      charger();
    } catch (e) {
      setMsg({ texte: e.response?.data?.detail || "Erreur lors de l'enregistrement",
               type: 'erreur' });
    }
  };

  return (
    <Section titre="Configuration du scan">
      <p style={s.hint}>
        Deux réglages de temps, qui décident du confort du barman au comptoir
        et de ce qui se passe quand un scan n'aboutit pas.
      </p>
      <div style={s.formulaire}>
        <Champ label="Délai de scan (minutes)" type="number" value={delai} onChange={setDelai}
          placeholder={`Sinon : ${defauts.delai_scan_minutes} min`}
          info="Temps laissé au barman pour scanner et valider les gobelets après l'encaissement. Passé ce délai, toute consigne payée sans gobelet validé est remboursée automatiquement au client — c'est le filet de sécurité : coupure réseau, terminal cassé, scan oublié. 15 minutes par défaut. Baisser cette valeur raccourcit l'attente du client en cas de problème, mais laisse moins de marge au barman." />
        <Champ label="Fenêtre du geste (secondes)" type="number" value={fenetre} onChange={setFenetre}
          placeholder={`Sinon : ${defauts.bracelet_fenetre_secondes} s`}
          info="Le barman passe ses verres et son bracelet en un seul mouvement, et le lecteur les renvoie dans le désordre. Ce réglage est l'écart maximum toléré entre les deux, dans les DEUX sens : les gobelets lus pendant les N secondes précédant le bracelet sont récupérés, et le lecteur reste aiguillé N secondes après pour ceux qui suivent. Ce n'est pas une durée de service : l'aiguillage se referme dès que la commande du barman est complète, en général au bout d'une seconde. 10 s par défaut ; l'augmenter n'aide que si le barman s'y prend en plusieurs fois, et allonge d'autant la période où le comptoir voisin ne récupère plus ses propres gobelets. Sans effet tant qu'aucune puce n'est déclarée dans l'onglet Bracelets barman." />
      </div>
      <div style={{ marginTop: 12 }}>
        <Bouton label="Enregistrer les réglages" onClick={sauvegarder} />
      </div>
      <Message texte={msg.texte} type={msg.type} />

      {resolu && (
        <p style={{ ...s.hint, marginTop: 12 }}>
          Actuellement en vigueur : remboursement automatique au bout de{' '}
          <strong>{resolu.delai_scan_minutes} min</strong>, fenêtre du geste de{' '}
          <strong>± {resolu.bracelet_fenetre_secondes} s</strong>.
        </p>
      )}
    </Section>
  );
}

function Lecteurs({ festival }) {
  const [bars, setBars]   = useState([]);
  const [tapis, setTapis] = useState([]);
  const [bacs, setBacs]   = useState([]);
  const [msg, setMsg]     = useState({ texte: '', type: '' });

  // Un seul formulaire ouvert à la fois : { type, barId?, editing? }.
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState({});
  const [msgModal, setMsgModal] = useState({ texte: '', type: '' });

  const charger = async () => {
    try {
      const [b, t, c] = await Promise.all([
        api.get(`/admin/bars/${festival.id}`),
        api.get(`/admin/tapis/${festival.id}`),
        api.get(`/admin/bacs/${festival.id}`),
      ]);
      setBars(b.data); setTapis(t.data); setBacs(c.data);
    } catch (e) { console.error(e); }
  };
  /* Rafraîchissement automatique (rev33, 09/09/2026).

     Les pastilles ne disent plus « configuré » mais « répond en ce
     moment ». Une information vivante affichée une seule fois serait
     pire que pas d'information : on lirait « en ligne » sur un lecteur
     débranché depuis vingt minutes.

     15 secondes : le collecteur rapporte toutes les 10 s et la console
     tolère 60 s d'ancienneté, donc l'écran ne peut jamais montrer un
     état de plus d'une minute et quart. La requête ne lit que trois
     petites tables. */
  useEffect(() => {
    charger();
    const minuteur = setInterval(charger, 15000);
    return () => clearInterval(minuteur);
  }, [festival]);

  const champ = (cle) => (v) => setForm(f => ({ ...f, [cle]: v }));

  const ouvrirBar = () => {
    setForm({ nom: '', localisation: '' });
    setMsgModal({ texte: '', type: '' }); setModal({ type: 'bar' });
  };
  const ouvrirTapis = (barId, t = null) => {
    setForm({
      identifiant_materiel: t?.identifiant_materiel || '',
      nom: t?.nom || '',
      partenaire_terminal_id: t?.partenaire_terminal_id || '',
      reader_ip: t?.reader_ip || '',
      reader_port: t?.reader_port ? String(t.reader_port) : '27011',
    });
    setMsgModal({ texte: '', type: '' }); setModal({ type: 'tapis', barId, editing: t });
  };
  const ouvrirBac = (b = null) => {
    setForm({
      nom: b?.nom || '', localisation: b?.localisation || '',
      identifiant_materiel: b?.identifiant_materiel || '',
      reader_ip: b?.reader_ip || '',
      reader_port: b?.reader_port ? String(b.reader_port) : '27011',
    });
    setMsgModal({ texte: '', type: '' }); setModal({ type: 'bac', editing: b });
  };

  const sauvegarder = async () => {
    const { type, barId, editing } = modal;
    const port = form.reader_port ? parseInt(form.reader_port, 10) : null;
    try {
      if (type === 'bar') {
        if (!form.nom) { setMsgModal({ texte: 'Nom obligatoire', type: 'erreur' }); return; }
        await api.post('/admin/bars', {
          nom: form.nom, localisation: form.localisation || null, festival_id: festival.id,
        });
      } else if (type === 'tapis') {
        if (!form.identifiant_materiel) { setMsgModal({ texte: 'Identifiant obligatoire', type: 'erreur' }); return; }
        const payload = {
          identifiant_materiel: form.identifiant_materiel, nom: form.nom || null,
          bar_id: barId, festival_id: festival.id,
          partenaire_terminal_id: form.partenaire_terminal_id || null,
          reader_ip: form.reader_ip || null, reader_port: port,
        };
        if (editing) await api.put(`/admin/tapis/${editing.id}`, payload);
        else await api.post('/admin/tapis', payload);
      } else {
        if (!form.nom || !form.identifiant_materiel) {
          setMsgModal({ texte: 'Nom et identifiant obligatoires', type: 'erreur' }); return;
        }
        const payload = {
          nom: form.nom, localisation: form.localisation || null,
          identifiant_materiel: form.identifiant_materiel, festival_id: festival.id,
          reader_ip: form.reader_ip || null, reader_port: port,
        };
        // PUT et non suppression/recréation : un bac recréé repartirait
        // avec ses compteurs de vidange à zéro.
        if (editing) await api.put(`/admin/bacs/${editing.id}`, payload);
        else await api.post('/admin/bacs', payload);
      }
      setModal(null); charger();
    } catch (e) {
      setMsgModal({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' });
    }
  };

  const supprimer = async (chemin) => {
    try { await api.delete(chemin); charger(); }
    catch (e) { setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' }); }
  };

  const tapisPret = (t) => Boolean(t.partenaire_terminal_id && t.reader_ip);
  const bacPret   = (b) => Boolean(b.reader_ip);

  const total        = tapis.length + bacs.length;
  const prets        = tapis.filter(tapisPret).length + bacs.filter(bacPret).length;
  const sansTerminal = tapis.filter(t => !t.partenaire_terminal_id).length;
  const sansIp       = tapis.filter(t => !t.reader_ip).length + bacs.filter(b => !b.reader_ip).length;

  /* ── L'état RÉEL des lecteurs (rev33, 09/09/2026) ────────────────
     La pastille disait « prêt » dès qu'un terminal et une IP étaient
     saisis. Elle restait donc verte sur un lecteur débranché — et,
     bien pire, sur un collecteur arrêté : la console entière semblait
     saine la nuit où plus rien ne scannait.

     Le collecteur rapporte désormais l'état de chaque lecteur toutes
     les dix secondes (`POST /verres/etat-lecteurs`). Au-delà d'une
     minute sans nouvelle, l'information n'en est plus une. */
  const FRAICHEUR_MS = 60000;
  const frais = (d) => Boolean(d) && (Date.now() - new Date(d).getTime()) < FRAICHEUR_MS;

  //   'regler'   il manque un terminal ou une IP — le collecteur ne
  //              connaît même pas ce lecteur ;
  //   'muet'     configuré, mais il ne répond pas ;
  //   'en_ligne' il a répondu il y a moins d'une minute.
  const etatLecteur = (l, pret) => {
    if (!pret) return 'regler';
    return frais(l.dernier_contact_at) ? 'en_ligne' : 'muet';
  };

  /* Le collecteur parle-t-il encore ? On regarde le rapport le plus
     récent, tous lecteurs confondus. Aucun rapport frais ne veut pas
     dire « cinq lecteurs sont tombés » mais « le PC de régie s'est
     arrêté » — deux pannes opposées, deux gestes opposés. C'est pour
     les distinguer que le collecteur envoie DEUX dates : celle où il
     a parlé, et celle où le lecteur a répondu. */
  const tousLecteurs = [...tapis, ...bacs];
  const collecteurVivant = tousLecteurs.some(l => frais(l.dernier_rapport_at));
  const dernierRapport = tousLecteurs
    .map(l => l.dernier_rapport_at)
    .filter(Boolean)
    .sort()
    .pop() || null;
  const enLigne = tapis.filter(t => etatLecteur(t, tapisPret(t)) === 'en_ligne').length
                + bacs.filter(b => etatLecteur(b, bacPret(b)) === 'en_ligne').length;

  const PASTILLE_LIBELLE = { regler: 'à régler', muet: 'muet', en_ligne: 'en ligne' };
  const PASTILLE_TON     = { regler: 'danger',   muet: 'alerte', en_ligne: 'succes' };
  const PASTILLE_STYLE   = { regler: 'pastilleAlerte', muet: 'pastilleMuet', en_ligne: 'pastilleOk' };

  // `titre` : le motif exact au survol — « connexion impossible
  // (code 48) ». Le mot « muet » dit qu'il y a un problème, l'infobulle
  // dit lequel, sans encombrer la colonne.
  const Pastille = ({ etat, titre }) => (
    <span style={s[PASTILLE_STYLE[etat]]} title={titre || undefined}>
      <span style={pastille(PASTILLE_TON[etat])} />
      {PASTILLE_LIBELLE[etat]}
    </span>
  );

  const Ligne = ({ label, valeur, manque }) => (
    <div style={s.carteLigne}>
      <span style={s.carteLabel}>{label}</span>
      {valeur ? <span style={s.carteValeur}>{valeur}</span>
              : <span style={s.carteManque}>{manque}</span>}
    </div>
  );

  return (
    <>
      <Section titre={`Lecteurs — ${festival.nom}`}>
        <div style={total === 0 ? s.bandeauNeutre : (sansTerminal + sansIp > 0 ? s.bandeauAlerte : s.bandeauOk)}>
          {total === 0
            ? "Aucun lecteur déclaré — commencez par créer un bar, puis ajoutez-y ses lecteurs."
            : <>
                <strong>{total} lecteur{total > 1 ? 's' : ''}</strong> · {prets} prêt{prets > 1 ? 's' : ''}
                {collecteurVivant && <> · <strong>{enLigne} en ligne</strong></>}
                {sansTerminal > 0 && <> · <strong>{sansTerminal} sans terminal</strong></>}
                {sansIp > 0 && <> · <strong>{sansIp} sans IP</strong></>}
                {sansTerminal + sansIp === 0 && " · tout est configuré"}
              </>}
        </div>

        {/* ── Le collecteur donne-t-il encore signe de vie ? (rev33) ──
            Sans ce bandeau, un PC de régie éteint afficherait cinq
            lecteurs muets et enverrait quelqu'un vérifier cinq câbles
            au bar. La panne est ailleurs, et elle se répare en régie.
            On ne l'affiche qu'une fois des lecteurs déclarés : sur un
            festival vide, il n'y a pas encore de collecteur à attendre. */}
        {total > 0 && !collecteurVivant && (
          <div style={s.bandeauAlerte}>
            <strong>Aucune nouvelle du collecteur.</strong>{' '}
            {dernierRapport
              ? <>Son dernier signe de vie remonte à {new Date(dernierRapport).toLocaleString('fr-FR')}.</>
              : <>Il n'a jamais rapporté son état depuis ce festival.</>}
            {' '}L'état des lecteurs ci-dessous n'est donc <strong>pas à jour</strong> : c'est le PC
            de régie qu'il faut regarder, pas les lecteurs.
          </div>
        )}
        <p style={s.hint}>
          Un lecteur sans <strong>terminal</strong> n'ouvrira jamais de widget : le terminal du
          partenaire ne sera pas reconnu, et les consignes de ses ventes partiront en remboursement
          automatique. Un lecteur sans <strong>IP</strong> reste invisible pour le collecteur, donc
          aucun gobelet ne peut y être scanné.
        </p>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <BoutonSm label="Nouveau bar" onClick={ouvrirBar} />
          <BoutonSm label="Nouveau bac de déconsigne" onClick={() => ouvrirBac()} />
        </div>
        {msg.texte && <Message texte={msg.texte} type={msg.type} />}

        {/* ── Deux camps, pas une file de colonnes ──────────────────
            Séparation gauche/droite ajoutée le 2026-09-04, à la demande
            de l'utilisateur : « je les confonds souvent et ça peut
            induire à la faute ». Les colonnes se suivaient sans rupture
            et rien ne disait où finissaient les bars — or une IP saisie
            dans la mauvaise colonne envoie les gobelets à l'opposé de
            ce qu'on croit, et la confusion ne se voit qu'en service.

            À gauche le gobelet PART, à droite il REVIENT. Le titre de
            chaque camp le dit en toutes lettres plutôt que de compter
            sur une nuance de couleur. */}
        <div className="jums-rail" style={s.colonnes}>
          <div style={s.groupeLecteurs}>
            <div style={s.groupeEntete}>
              <span style={s.groupeTitre}>Bars — le gobelet part</span>
              <span style={s.groupeCompte}>
                {tapis.length} lecteur{tapis.length > 1 ? 's' : ''}
              </span>
            </div>
            <div style={s.groupeRang}>
          {bars.map(bar => {
            const liste = tapis.filter(t => t.bar_id === bar.id);
            const aRegler = liste.filter(t => !tapisPret(t)).length;
            return (
              <div key={bar.id} style={s.colonne}>
                <div style={s.colonneEntete}>
                  <div style={{ minWidth: 0 }}>
                    <div style={s.colonneTitre}>{bar.nom}</div>
                    <div style={s.colonneSous}>
                      {liste.length} lecteur{liste.length > 1 ? 's' : ''}
                      {aRegler > 0 ? ` · ${aRegler} à régler` : (liste.length > 0 ? ' · tous prêts' : '')}
                      {bar.localisation ? ` · ${bar.localisation}` : ''}
                    </div>
                  </div>
                  <BoutonSupprimer onClick={() => supprimer(`/admin/bars/${bar.id}`)} />
                </div>

                {liste.length === 0 && <p style={s.colonneVide}>Aucun lecteur</p>}
                {liste.map(t => (
                  <div key={t.id} style={s.carteLecteur}>
                    <div style={s.carteEntete}>
                      <span style={s.carteId}>{t.identifiant_materiel}</span>
                      <Pastille etat={etatLecteur(t, tapisPret(t))}
                                titre={t.derniere_erreur_lecteur} />
                    </div>
                    {t.nom && <div style={s.carteNom}>{t.nom}</div>}
                    <Ligne label="Terminal" valeur={t.partenaire_terminal_id} manque="non associé" />
                    <Ligne label="Lecteur"
                      valeur={t.reader_ip ? `${t.reader_ip}:${t.reader_port}` : null} manque="pas d'IP" />
                    <div style={s.carteActions}>
                      <BoutonSm label="Modifier" onClick={() => ouvrirTapis(bar.id, t)} />
                      <BoutonSupprimer onClick={() => supprimer(`/admin/tapis/${t.id}`)} />
                    </div>
                  </div>
                ))}

                <button style={s.colonneAjout} onClick={() => ouvrirTapis(bar.id)}>
                  Ajouter un lecteur
                </button>
              </div>
            );
          })}

            </div>
          </div>

          <div style={s.separateurLecteurs} aria-hidden="true" />

          {/* Les bacs sont des lecteurs comme les autres pour le collecteur :
              même IP, même port, même DLL. Ils méritent leur colonne ici
              plutôt qu'un onglet séparé — c'est le même matériel à vérifier
              avant l'ouverture.

              Mais ils font l'INVERSE du bar : ici le gobelet revient et
              l'argent ressort. D'où le camp de droite, le filet qui l'en
              sépare et le liseré d'accent sur la colonne. */}
          <div style={s.groupeLecteurs}>
            <div style={s.groupeEntete}>
              <span style={{ ...s.groupeTitre, color: couleurs.accent }}>
                Bacs — le gobelet revient
              </span>
              <span style={s.groupeCompte}>
                {bacs.length} lecteur{bacs.length > 1 ? 's' : ''}
              </span>
            </div>
            <div style={s.groupeRang}>
          <div key="bacs" style={s.colonneBac}>
            <div style={s.colonneEntete}>
              <div style={{ minWidth: 0 }}>
                <div style={s.colonneTitre}>Bacs de déconsigne</div>
                <div style={s.colonneSous}>
                  {bacs.length} lecteur{bacs.length > 1 ? 's' : ''}
                  {bacs.filter(b => !bacPret(b)).length > 0
                    ? ` · ${bacs.filter(b => !bacPret(b)).length} à régler`
                    : (bacs.length > 0 ? ' · tous prêts' : '')}
                </div>
              </div>
            </div>

            {bacs.length === 0 && <p style={s.colonneVide}>Aucun bac</p>}
            {bacs.map(b => (
              <div key={b.id} style={s.carteLecteur}>
                <div style={s.carteEntete}>
                  <span style={s.carteId}>{b.identifiant_materiel}</span>
                  <Pastille etat={etatLecteur(b, bacPret(b))}
                            titre={b.derniere_erreur_lecteur} />
                </div>
                <div style={s.carteNom}>{b.nom}{b.localisation ? ` · ${b.localisation}` : ''}</div>
                <Ligne label="Lecteur"
                  valeur={b.reader_ip ? `${b.reader_ip}:${b.reader_port}` : null} manque="pas d'IP" />
                <div style={s.carteActions}>
                  <BoutonSm label="Modifier" onClick={() => ouvrirBac(b)} />
                  <BoutonSupprimer onClick={() => supprimer(`/admin/bacs/${b.id}`)} />
                </div>
              </div>
            ))}

            <button style={s.colonneAjout} onClick={() => ouvrirBac()}>
              Ajouter un bac
            </button>
          </div>
            </div>
          </div>
        </div>
      </Section>

      <ConfigurationScan festival={festival} />

      {modal && (
        <Modal
          titre={
            modal.type === 'bar' ? 'Nouveau bar'
            : modal.type === 'tapis' ? (modal.editing ? 'Modifier le lecteur' : 'Nouveau lecteur')
            : (modal.editing ? 'Modifier le bac' : 'Nouveau bac de déconsigne')
          }
          onClose={() => setModal(null)}>
          <div style={s.formulaire}>
            {modal.type === 'bar' && <>
              <Champ label="Nom *" value={form.nom} onChange={champ('nom')} placeholder="Ex: Grand Bar" />
              <Champ label="Localisation" value={form.localisation} onChange={champ('localisation')} placeholder="Optionnel" />
            </>}

            {modal.type === 'tapis' && <>
              <Champ label="Identifiant matériel *" value={form.identifiant_materiel}
                onChange={champ('identifiant_materiel')} placeholder="Ex: GB_T1"
                info="Le nom que le collecteur utilise pour retrouver ce lecteur au démarrage. C'est aussi ce qui s'affiche en haut du widget, sous le logo." />
              <Champ label="Nom (optionnel)" value={form.nom} onChange={champ('nom')} placeholder="Ex: Tapis du comptoir" />
              <Champ label="ID Terminal partenaire" value={form.partenaire_terminal_id}
                onChange={champ('partenaire_terminal_id')} placeholder="Ex: TRM-000"
                info="L'identifiant du terminal Festicash associé à CE lecteur. Un terminal = un lecteur, la correspondance est stricte. Sans lui, le widget ne s'ouvrira jamais sur ce lecteur : le terminal ne sera pas reconnu, et les consignes de ses ventes seront remboursées automatiquement à l'échéance." />
              <Champ label="IP du lecteur RFID" value={form.reader_ip} onChange={champ('reader_ip')}
                placeholder="Ex: 192.168.0.250"
                info="Adresse réseau du lecteur FR2000 branché sur ce tapis. Le collecteur s'y connecte au démarrage. Attention : cette adresse doit être joignable depuis le PC qui fait tourner le collecteur — même sous-réseau." />
              <Champ label="Port du lecteur" type="number" value={form.reader_port} onChange={champ('reader_port')} placeholder="27011" />
            </>}

            {modal.type === 'bac' && <>
              <Champ label="Nom *" value={form.nom} onChange={champ('nom')} placeholder="Ex: Bac Entrée Nord" />
              <Champ label="Localisation" value={form.localisation} onChange={champ('localisation')} placeholder="Optionnel" />
              <Champ label="Identifiant matériel *" value={form.identifiant_materiel}
                onChange={champ('identifiant_materiel')} placeholder="Ex: BAC-01" />
              <Champ label="IP du lecteur RFID" value={form.reader_ip} onChange={champ('reader_ip')}
                placeholder="Ex: 192.168.0.60"
                info="Adresse réseau du lecteur FR2000 branché sur ce bac. Un bac n'a pas de terminal : il ne fait que constater les retours de gobelets, ce qui déclenche le remboursement du client." />
              <Champ label="Port du lecteur" type="number" value={form.reader_port} onChange={champ('reader_port')} placeholder="27011" />
            </>}

            <Bouton label={modal.editing ? 'Enregistrer les modifications' : 'Créer'} onClick={sauvegarder} />
            <Message texte={msgModal.texte} type={msgModal.type} />
          </div>
        </Modal>
      )}
    </>
  );
}


// ─── BRACELETS BARMAN (rev20) ───────────────────────────────
// Une puce RFID au poignet du barman, rattachée à SON terminal. Passée
// sur n'importe quel lecteur, elle y aiguille les gobelets vers la
// commande de ce terminal — c'est ce qui permet de servir sur un
// comptoir dont le lecteur n'est pas celui du terminal qui a encaissé.
//
// Une ligne = UNE PUCE. Deux puces sur le même bracelet (pour être sûr
// qu'au moins une soit lue) = deux lignes pointant le même lecteur.

function Bracelets({ festival }) {
  const [bracelets, setBracelets] = useState([]);
  const [tapis, setTapis]         = useState([]);
  const [etatDirect, setEtatDirect] = useState({ sessions: [], en_attente: [] });
  const [msg, setMsg]             = useState({ texte: '', type: '' });

  const [modal, setModal]         = useState(null);   // { editing? }
  const [form, setForm]           = useState({});
  const [msgModal, setMsgModal]   = useState({ texte: '', type: '' });

  const charger = async () => {
    try {
      const [b, t] = await Promise.all([
        api.get(`/admin/bracelets/${festival.id}`),
        api.get(`/admin/tapis/${festival.id}`),
      ]);
      setBracelets(b.data); setTapis(t.data);
    } catch (e) { console.error(e); }
  };

  const chargerEtat = async () => {
    try { setEtatDirect((await api.get(`/admin/bracelets-sessions/${festival.id}`)).data); }
    catch (e) { console.error(e); }
  };

  useEffect(() => {
    charger(); chargerEtat();
    // 5 s : c'est un écran de diagnostic qu'on regarde pendant qu'un
    // barman teste son bracelet devant soi — au-delà, on ne sait plus
    // si le geste a marché ou si l'écran est en retard.
    const i = setInterval(chargerEtat, 5000);
    return () => clearInterval(i);
  }, [festival]);

  const champ = (cle) => (v) => setForm(f => ({ ...f, [cle]: v }));

  const ouvrir = (b = null) => {
    setForm({
      rfid_uid: b?.rfid_uid || '',
      tapis_id: b?.tapis_id || (tapis[0]?.id || ''),
      libelle: b?.libelle || '',
      actif: b ? b.actif : true,
    });
    setMsgModal({ texte: '', type: '' }); setModal({ editing: b });
  };

  const sauvegarder = async () => {
    if (!form.rfid_uid?.trim()) { setMsgModal({ texte: 'EPC de la puce obligatoire', type: 'erreur' }); return; }
    if (!form.tapis_id) { setMsgModal({ texte: 'Choisissez le terminal du barman', type: 'erreur' }); return; }
    const payload = {
      festival_id: festival.id,
      tapis_id: form.tapis_id,
      rfid_uid: form.rfid_uid.trim().toUpperCase(),
      libelle: form.libelle || null,
      actif: form.actif !== false,
    };
    try {
      if (modal.editing) await api.put(`/admin/bracelets/${modal.editing.id}`, payload);
      else await api.post('/admin/bracelets', payload);
      setModal(null); charger();
    } catch (e) {
      setMsgModal({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' });
    }
  };

  const supprimer = async (id) => {
    try { await api.delete(`/admin/bracelets/${id}`); charger(); }
    catch (e) { setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' }); }
  };

  const basculerActif = async (b) => {
    try {
      await api.put(`/admin/bracelets/${b.id}`, {
        festival_id: festival.id, tapis_id: b.tapis_id,
        rfid_uid: b.rfid_uid, libelle: b.libelle, actif: !b.actif,
      });
      charger();
    } catch (e) { setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' }); }
  };

  const actifs = bracelets.filter(b => b.actif);
  const enAttente = etatDirect.en_attente.reduce((n, a) => n + Number(a.nb), 0);
  const secondesRestantes = (iso) =>
    Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000));

  return (
    <>
      <Section titre={`Bracelets barman — ${festival.nom}`} onAjouter={tapis.length ? () => ouvrir() : null}>
        <div style={actifs.length === 0 ? s.bandeauNeutre : s.bandeauOk}>
          {actifs.length === 0
            ? "Aucun bracelet déclaré — les lecteurs ne servent que la commande de leur propre terminal (comportement d'origine)."
            : <><strong>{actifs.length} puce{actifs.length > 1 ? 's' : ''} active{actifs.length > 1 ? 's' : ''}</strong>
                {' '}· {new Set(actifs.map(b => b.tapis_id)).size} terminal(aux) couvert(s)</>}
        </div>
        <p style={s.hint}>
          Le barman avance la main avec <strong>ses verres et son poignet en même temps</strong> au-dessus
          du lecteur qu'il a sous la main : ces gobelets partent sur la commande de <strong>son</strong>{' '}
          terminal, et non sur celle du terminal associé à ce lecteur. <strong>L'ordre n'a aucune
          importance</strong> — le bracelet récupère les gobelets lus juste avant lui, y compris ceux que
          la commande du comptoir venait d'absorber, et couvre ceux qui arrivent juste après.
          L'aiguillage se referme <strong>dès que la commande du barman est complète</strong> ; la
          « fenêtre du geste » de « Config partenaire » n'est qu'un plafond de sécurité.
        </p>
        {tapis.length === 0 && (
          <p style={bandeau('alerte')}>
            Créez d'abord un lecteur dans l'onglet « Bars &amp; lecteurs » : un bracelet désigne
            toujours le terminal d'un lecteur existant.
          </p>
        )}
        <Message texte={msg.texte} type={msg.type} />

        {/* ── Ce qui se passe à l'instant ─────────────────────────
            Sans cet encadré, un bracelet mal réglé ne se remarque que
            par des gobelets « qui ne comptent pas », sans dire pourquoi. */}
        <div style={{ marginBottom: 20, padding: 14, background: couleurs.surfaceDouce, border: `1px solid ${couleurs.bord}`, borderRadius: 10 }}>
          <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: couleurs.encre }}>
            En direct — aiguillages en cours
          </p>
          {etatDirect.sessions.length === 0
            ? <p style={{ fontSize: 12, color: couleurs.encreFaible, margin: 0 }}>
                Aucun aiguillage ouvert : chaque lecteur sert sa propre commande. C'est l'état
                normal — un aiguillage se referme dès que la commande du barman est complète,
                il ne dure donc qu'une seconde ou deux.
              </p>
            : (
              <div style={s.liste}>
                {etatDirect.sessions.map((sess, i) => (
                  <div key={i} style={{ ...s.listeItem, background: couleurs.accentDoux, borderColor: couleurs.accent }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{sess.lecteur}</span>
                    <span style={{ margin: '0 8px', color: couleurs.encreFaible }}>→</span>
                    <strong style={{ fontFamily: 'monospace', fontSize: 13 }}>
                      {sess.terminal || sess.cible}
                    </strong>
                    <span style={badge('accent')}>
                      encore {secondesRestantes(sess.expire_at)} s
                    </span>
                    <span style={{ ...s.badge, fontFamily: 'monospace' }}>{sess.bracelet_uid}</span>
                  </div>
                ))}
              </div>
            )}
          {enAttente > 0 && (
            <p style={{ margin: '10px 0 0', fontSize: 12.5, color: couleurs.alerte }}>
              {enAttente} gobelet(s) posé(s) sans destination attendent qu'on passe un bracelet
              {' '}({etatDirect.en_attente.map(a => `${a.lecteur} : ${a.nb}`).join(' · ')}).
            </p>
          )}
        </div>

        {bracelets.length === 0
          ? <p style={s.vide}>Aucune puce enregistrée — utilisez le bouton + pour en ajouter une.</p>
          : (
            <div style={s.liste}>
              {bracelets.map(b => (
                <div key={b.id} style={s.listeItem}>
                  <div style={s.festivalItem}>
                    <div>
                      <strong style={{ fontFamily: 'monospace' }}>{b.rfid_uid}</strong>
                      <span style={badgeMono('accent')}>
                        {b.partenaire_terminal_id || 'terminal non renseigné'}
                      </span>
                      <span style={{ ...s.badge, fontFamily: 'monospace' }}>{b.tapis_identifiant}</span>
                      {b.bar_nom && <span style={s.badge}>{b.bar_nom}</span>}
                      {!b.actif && <span style={badge('danger')}>désactivée</span>}
                      {b.libelle && (
                        <p style={{ fontSize: 12.5, color: couleurs.encreDouce, margin: '6px 0 0' }}>{b.libelle}</p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <BoutonSm label={b.actif ? 'Désactiver' : 'Réactiver'} onClick={() => basculerActif(b)}
                        />
                      <BoutonSm label="Modifier" onClick={() => ouvrir(b)} />
                      <BoutonSupprimer onClick={() => supprimer(b.id)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </Section>

      {modal && (
        <Modal titre={modal.editing ? 'Modifier la puce' : 'Nouvelle puce de bracelet'}
          onClose={() => setModal(null)}>
          <div style={s.formulaire}>
            <Champ label="EPC de la puce *" value={form.rfid_uid} onChange={champ('rfid_uid')}
              placeholder="Ex: E2806A960000502E104924F5"
              info="L'identifiant lu par le lecteur RFID, exactement comme pour un gobelet. Le plus simple : passer le bracelet sur un lecteur et relever l'EPC dans le journal du collecteur. Cet EPC ne doit PAS être enregistré comme gobelet — l'enregistrement sera refusé des deux côtés." />
            <div style={s.champ}>
              <label style={s.label}>
                Terminal du barman *
                <InfoBulle texte="Le terminal sur lequel CE barman encaisse. C'est la commande de ce terminal que recevront les gobelets posés là où il passe son bracelet. La liste montre les lecteurs du festival, avec l'ID Terminal partenaire de chacun." />
              </label>
              <select style={s.input} value={form.tapis_id} onChange={e => champ('tapis_id')(e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {tapis.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.partenaire_terminal_id || '(terminal non renseigné)'} — {t.identifiant_materiel}
                    {t.bar_nom ? ` (${t.bar_nom})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <Champ label="Libellé (optionnel)" value={form.libelle} onChange={champ('libelle')}
              placeholder="Ex: bracelet de Marie — puce A" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="bracelet-actif" checked={form.actif !== false}
                onChange={e => champ('actif')(e.target.checked)} />
              <label htmlFor="bracelet-actif" style={{ ...s.label, margin: 0 }}>
                Puce active
                <InfoBulle texte="Décochée, la puce est ignorée par les lecteurs sans être supprimée — c'est ce qu'il faut faire d'un bracelet perdu, pour garder la trace de ce qu'il désignait." />
              </label>
            </div>
            <Bouton label={modal.editing ? 'Enregistrer les modifications' : 'Ajouter la puce'} onClick={sauvegarder} />
            <Message texte={msgModal.texte} type={msgModal.type} />
          </div>
        </Modal>
      )}
    </>
  );
}


// ─── BACS ──────────────────────────────────────────────────

// ─── COMMANDES ACTIVES ──────────────────────────────────────
// Une commande ouverte (widget non fermé, test interrompu, fenêtre
// fermée sans valider...) bloque toute nouvelle vente sur le même
// bar/tapis (protection anti-doublon côté webhook). Cette vue permet
// de les repérer et de les débloquer sans toucher à la base.
function tempsEcoule(dateStr) {
  const secondes = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (secondes < 60) return `${secondes}s`;
  const minutes = Math.floor(secondes / 60);
  if (minutes < 60) return `${minutes} min`;
  const heures = Math.floor(minutes / 60);
  return `${heures} h ${minutes % 60} min`;
}

/* Compte à rebours jusqu'à l'échéance. « — » si elle est absente,
   « échue » si elle est passée : une commande dont le délai est dépassé
   attend seulement le prochain passage du moteur, ce n'est pas une
   erreur. (rev24) */
function restant(echeanceAt, maintenant) {
  if (!echeanceAt) return '—';
  const s = Math.round((new Date(echeanceAt) - maintenant) / 1000);
  if (s <= 0) return 'échue';
  const m = Math.floor(s / 60);
  return m > 0 ? `${m} min ${String(s % 60).padStart(2, '0')} s` : `${s} s`;
}

function Commandes({ festival }) {
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [msg, setMsg]             = useState({ texte: '', type: '' });
  // Horloge locale : le compte à rebours doit descendre chaque seconde,
  // alors que la liste ne se recharge que toutes les 5 s. Deux rythmes
  // distincts, une seule requête réseau — l'échéance est une date
  // absolue, on n'a rien à redemander au serveur pour la décompter.
  const [maintenant, setMaintenant] = useState(() => new Date());

  const charger = async () => {
    try { setCommandes((await api.get(`/admin/commandes-actives/${festival.id}`)).data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    charger();
    const i = setInterval(charger, 5000);
    return () => clearInterval(i);
  }, [festival]);

  useEffect(() => {
    const h = setInterval(() => setMaintenant(new Date()), 1000);
    return () => clearInterval(h);
  }, []);

  const annuler = async (ordreId) => {
    try {
      await api.post(`/popup/ordre/${ordreId}/annuler`);
      setMsg({ texte: 'Commande annulée — le bar/tapis est de nouveau libre.', type: 'succes' });
      charger();
    } catch (e) {
      setMsg({ texte: e.response?.data?.detail || "Erreur lors de l'annulation", type: 'erreur' });
    }
  };

  return (
    <Section titre={`Commandes actives — ${festival.nom}`}>
      <p style={s.hint}>
        Une commande apparaît ici tant qu'elle n'a pas été validée, annulée ou remboursée côté
        barman/widget. Elle <strong>bloque toute nouvelle vente</strong> sur le même bar/tapis
        (protection anti-doublon). Annulez-la ici si elle est restée coincée — fenêtre fermée sans
        valider, test interrompu, connexion perdue.
      </p>
      <Message texte={msg.texte} type={msg.type} />
      {loading ? (
        <p style={s.vide}>Chargement...</p>
      ) : commandes.length === 0 ? (
        <p style={s.vide}>Aucune commande active — tous les bars/tapis sont libres.</p>
      ) : (
        <div style={s.liste}>
          {commandes.map(c => {
            // Née du seul webhook : le terminal était hors ligne, aucun
            // widget n'a pu s'ouvrir. Rien à faire, rien à débloquer —
            // elle attend l'échéance qui remboursera. On la grise pour
            // dire « pas d'action ici », sans effacer l'information :
            // un terminal muet mérite d'être vu. (rev24)
            const orpheline = c.route_b && !c.route_a;
            // Le webhook part du serveur du partenaire et peut arriver
            // quelques secondes après la vente. On ne s'alarme donc
            // qu'au-delà de 90 s — sans ce délai, chaque vente normale
            // afficherait une alerte pendant sa première seconde.
            const ageS = (maintenant - new Date(c.created_at)) / 1000;
            return (
            <div key={c.id} style={{ ...s.listeItem, ...(orpheline ? { opacity: 0.72 } : {}) }}>
              <div style={s.festivalItem}>
                <div>
                  <strong>{c.bar_nom}</strong>
                  <span style={badgeMono('accent')}>
                    {c.tapis_identifiant || 'sans tapis'}
                  </span>
                  <span style={badge(c.statut === 'en_attente' ? 'alerte' : 'accent')}>
                    {c.statut}
                  </span>
                  {!orpheline && (
                    <span style={s.badge}>{c.verres_scannes}/{c.total_verres} scanné(s)</span>
                  )}
                  <span style={{ ...s.badge, background: couleurs.neutreDoux, color: couleurs.encreDouce }}>
                    ouverte depuis {tempsEcoule(c.created_at)}
                  </span>

                  {/* Commande normale : le widget est ouvert. Reste à
                      savoir si le filet de sécurité a joué lui aussi. */}
                  {c.route_a && (
                    c.route_b ? (
                      <span style={badge('succes')} title="Le webhook de la même transaction est bien arrivé : la vente est connue deux fois, elle ne peut plus être perdue.">
                        webhook reçu
                      </span>
                    ) : ageS < 90 ? (
                      <span style={{ ...s.badge, background: couleurs.neutreDoux, color: couleurs.encreDouce }}
                            title="Le webhook part du serveur du partenaire, il arrive généralement dans la minute.">
                        webhook en attente
                      </span>
                    ) : (
                      <span style={badge('alerte')} title="Passé une minute et demie, le webhook aurait dû arriver. Sans lui, cette vente n'a qu'un seul témoin : si le widget se ferme mal, plus rien ne la rattrape.">
                        webhook jamais reçu
                      </span>
                    )
                  )}

                  {/* Orpheline : pas de widget, donc pas de scan possible.
                      Le seul horizon utile est l'échéance — on la décompte
                      en direct plutôt que d'afficher une date à convertir
                      de tête à 2 h du matin. */}
                  {orpheline && (
                    <>
                      <span style={badge('alerte')} title="Le terminal était hors ligne au moment de la vente : aucun widget n'a pu s'ouvrir, aucun gobelet ne peut être scanné.">
                        webhook seul — pas de widget
                      </span>
                      <span style={{ ...s.badge, background: couleurs.neutreDoux, color: couleurs.encreDouce }}>
                        remboursement dans {restant(c.echeance_at, maintenant)}
                      </span>
                    </>
                  )}
                  <p style={{ fontSize: 12, color: couleurs.encreFaible, margin: '6px 0 0', fontFamily: 'monospace' }}>
                    bracelet {c.rfid_bracelet} · transaction {c.partenaire_transaction_id}
                  </p>
                </div>
                <BoutonAnnulerCommande onClick={() => annuler(c.id)} />
              </div>
            </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}


// ─── CONFIGURATION PARTENAIRE ───────────────────────────────
// Remplace les variables d'environnement globales (secret de signature,
// identifiants API partenaire, CONSIGNE_MONTANT...) — désormais éditable par festival.
// Un champ laissé vide repasse sur la valeur .env de secours (affichée
// en placeholder), pas destinée à un vrai festival en production.
function ConfigPartenaire({ festival }) {
  const [defauts, setDefauts] = useState(null);
  const [resolu, setResolu]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState({ texte: '', type: '' });

  const [refundApiUrl, setRefundApiUrl]             = useState('');
  const [nomPartenaire, setNomPartenaire]           = useState('');
  const [signatureSecret, setSignatureSecret]       = useState('');
  // Interrupteur séparé du secret (rev16) : couper la vérification le
  // temps d'un test sans effacer — donc sans perdre — la clé partenaire.
  const [signatureActive, setSignatureActive]       = useState(true);

  const charger = async () => {
    setLoading(true);
    try {
      const { config, resolu, defauts_env } = (await api.get(`/admin/partenaire-config/${festival.id}`)).data;
      setResolu(resolu);
      setDefauts(defauts_env);
      setRefundApiUrl(config.refund_api_url || '');
      setNomPartenaire(config.nom_partenaire || '');
      setSignatureSecret(config.signature_secret || '');
      // null (jamais réglé) = repli sur le .env, qui vérifie par défaut :
      // seul un false explicite décoche la case.
      setSignatureActive(config.signature_active !== false);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { charger(); }, [festival]);

  const sauvegarder = async () => {
    setMsg({ texte: '', type: '' });
    try {
      // Seuls les champs de CET écran sont envoyés. Depuis le
      // 01/09/2026, l'API n'écrit que ce qu'elle reçoit — sans quoi
      // enregistrer ici effacerait les réglages de scan, qui vivent
      // maintenant dans l'onglet Bars et lecteurs mais dans la même
      // ligne de configuration.
      await api.put(`/admin/partenaire-config/${festival.id}`, {
        refund_api_url: refundApiUrl,
        nom_partenaire: nomPartenaire,
        signature_secret: signatureSecret,
        signature_active: signatureActive,
      });
      setMsg({ texte: 'Configuration enregistrée.', type: 'succes' });
      charger();
    } catch (e) {
      setMsg({ texte: e.response?.data?.detail || 'Erreur lors de l\'enregistrement', type: 'erreur' });
    }
  };

  if (loading || !defauts) {
    return <Section titre={`Configuration partenaire — ${festival.nom}`}><p style={s.vide}>Chargement...</p></Section>;
  }

  return (
    <Section titre={`Configuration partenaire — ${festival.nom}`}>
      <p style={s.hint}>
        Propre à <strong>ce festival</strong> — plusieurs festivals peuvent utiliser des partenaires
        cashless différents (ou le même avec des identifiants différents) sans se gêner. Un champ laissé
        vide utilise la valeur affichée en filigrane (configuration technique de secours, pas destinée
        à un vrai festival).
      </p>

      <p style={s.sousTitre}>Identité du partenaire</p>
      <div style={s.formulaire}>
        <Champ label="Nom du partenaire" value={nomPartenaire} onChange={setNomPartenaire}
          placeholder="Ex: Partenaire principal, Partenaire test"
          info="Purement informatif — aucune valeur technique n'en dépend. Sert juste à savoir en un coup d'œil avec quel partenaire ce festival travaille réellement, plutôt qu'une grille de champs techniques sans étiquette." />
      </div>

      <p style={s.sousTitre}>Signature des appels du partenaire</p>
      <div style={s.formulaire}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <input type="checkbox" id="signature-active" checked={signatureActive}
            onChange={e => setSignatureActive(e.target.checked)} />
          <label htmlFor="signature-active" style={{ ...s.label, margin: 0 }}>
            Vérifier la signature des appels du partenaire
            <InfoBulle texte="L'interrupteur, séparé du secret ci-dessous. Décoché, JUMS accepte les appels du partenaire sans vérifier leur signature — pratique pour tester (avec le simulateur, ou avec un terminal Festicash pas encore configuré côté partenaire) SANS avoir à effacer le secret puis à le retaper à l'identique. Le secret reste enregistré tel quel : il suffit de recocher pour que tout redevienne vérifié. À laisser coché en festival réel : depuis le 31/08/2026 c'est la SEULE protection des trois routes, l'ancien secret webhook ayant été supprimé." />
          </label>
        </div>
        {!signatureActive && (
          <p style={{ ...s.hint, margin: '0 0 6px', color: couleurs.alerte }}>
            Vérification suspendue pour ce festival : n'importe quel appel vers
            <code> /consigne </code>, <code> /fermewidget </code> et
            <code> /webhook/paiement </code> est accepté, signé ou non.
            À ne pas laisser ainsi en production.
          </p>
        )}
        <Champ label="Secret de signature (HMAC)" type="password"
          value={signatureSecret} onChange={setSignatureSecret}
          placeholder="Vide = signature non vérifiée"
          info="Sécurise LES TROIS routes du partenaire : l'ouverture du widget, sa fermeture, et le webhook de paiement. Le partenaire signe ses appels avec ce secret, JUMS vérifie la signature avant de faire quoi que ce soit. Laissé vide, aucune vérification n'est faite (comme si la case ci-dessus était décochée). À remplir avant un vrai festival avec la même valeur que celle configurée côté partenaire — et pour couper temporairement le contrôle, décocher la case plutôt que vider ce champ, pour ne pas perdre la valeur." />
      </div>

      {/* Ménage du 2026-08-16 : les sections « Widget » (URL de base) et
          « Fermeture du widget » (URL de callback) ont disparu. Le
          terminal ouvre lui-même la page /consigne et referme sa fenêtre
          nativement depuis rev15 — ces deux adresses n'étaient plus
          jamais utilisées, seulement enregistrées. */}

      {/* « Identifiant client » et « Secret client » retirés le
          01/09/2026 : l'article 4 de la fiche Festicash n'authentifie
          que par la signature HMAC, avec le secret déjà saisi plus haut.
          Ces deux champs n'authentifiaient plus rien, et un champ qui ne
          sert à rien finit toujours par être rempli de travers. */}
      <p style={s.sousTitre}>Déclaration des consignes rendues</p>
      <div style={s.formulaire}>
        <Champ label="URL consigne rendue" value={refundApiUrl} onChange={setRefundApiUrl}
          placeholder={defauts.refund_api_url || 'Vide = remboursement simulé, aucun appel réel'}
          info="Adresse Festicash à laquelle JUMS déclare qu'une consigne a été rendue, quand un gobelet revient au bac de déconsigne. Fournie par Festicash. Tant qu'elle est vide — ou tant que le secret de signature ci-dessus l'est — JUMS simule les remboursements sans aucun appel réseau." />
      </div>

      {/* ── Bloc « Consigne » supprimé le 01/09/2026 ──────────────────
          « Montant de la consigne » n'existe plus du tout : il n'y a PAS
          un montant par festival. Un gobelet, un pichet et une écocup
          n'ont jamais valu la même chose, et chaque produit porte son
          prix dans l'onglet Produits depuis rev25. Ce champ ne servait
          plus qu'à faire croire le contraire — et à rembourser 2 € un
          pichet encaissé 5 €.

          « Délai de scan » et « Fenêtre du geste » sont partis dans
          l'onglet Bars et lecteurs, bloc « Configuration du scan » : ce
          sont des réglages du geste au comptoir, pas de la relation avec
          le partenaire. Ils écrivent dans la même table, d'où
          l'enregistrement partiel côté API. */}

      {/* Ménage du 2026-08-16 : la section « Matériel » (clé des lecteurs
          par festival) a été retirée. Le champ était explicitement
          décoratif — les endpoints de scan opposent la variable GLOBALE
          DEVICE_KEY du .env. Le laisser laissait croire à une protection
          par festival qui n'existait pas. */}

      <div style={{ marginTop: 12 }}>
        <Bouton label="Enregistrer la configuration" onClick={sauvegarder} />
      </div>
      <Message texte={msg.texte} type={msg.type} />

      {resolu && (
        <div style={{ marginTop: 20, padding: 14, background: couleurs.surfaceDouce, borderRadius: 10, border: `1px solid ${couleurs.bord}` }}>
          <p style={{ fontWeight: 600, fontSize: 13, margin: '0 0 4px', color: couleurs.encre }}>
            Valeurs réellement actives pour ce festival, à l'instant
          </p>
          <p style={{ ...s.hint, margin: '0 0 10px', fontSize: 12 }}>
            Que le champ ci-dessus soit rempli ou vide (repli sur le .env), c'est <strong>ceci</strong> qui part
            réellement dans chaque appel. En cas d'échec webhook ou remboursement côté partenaire (401, etc.),
            compare ces valeurs avec celles affichées sur le tableau de bord du partenaire — plutôt que d'aller
            lire des fichiers <code>.env</code> à la main sur deux machines.
          </p>
          <ValeurActive label="Partenaire" valeur={resolu.nom_partenaire} />
          <ValeurActive label="Vérification de signature"
            valeur={resolu.signature_active === false ? 'DÉSACTIVÉE (tout appel accepté)'
              : (resolu.signature_secret ? 'active' : 'sans effet (aucun secret configuré)')} />
          <ValeurActive label="Secret de signature (HMAC)" valeur={resolu.signature_secret} masque />
          <ValeurActive label="URL consigne rendue" valeur={resolu.refund_api_url} />
          {/* Les deux réglages de scan sont récapitulés dans l'onglet
              Bars et lecteurs, là où ils se règlent désormais. Les
              afficher aussi ici obligerait à tenir deux récapitulatifs
              en accord, pour rien. */}
        </div>
      )}
    </Section>
  );
}


// ─── PRODUITS ──────────────────────────────────────────────

/* ── Les deux sens du catalogue (rev28) ────────────────────────────
   AU NIVEAU DU MODULE, comme `cacheGobelets` plus bas et pour la même
   raison : une constante déclarée dans le composant mais lue plus haut
   dans le rendu lève « Cannot access before initialization » et blanchit
   la page — le build ne peut rien en dire, l'erreur n'existe qu'à
   l'exécution (constat du 01/09/2026).

   Pourquoi deux sens : le message d'une vente Festicash ne transporte
   que `<uuid>:<quantité>`, sans prix. Rien n'y distingue un retour de
   consigne, et un identifiant non reconnu vaut « 1 gobelet à scanner ».
   C'est ce catalogue, et lui seul, qui permet de faire la différence. */
/* Les types de contenants — MÊME LISTE que app/contenants.py.

   Fermée volontairement : le nom du produit relie les puces à leur
   produit, et une faute de frappe dans ce nom orphelinait d'un coup
   toutes les puces concernées — refusées au scan, en plein service.
   Un menu rend cette faute impossible.

   `autre` existe pour qu'un festival servant une écocup, un magnum ou
   une bouteille consignée ne soit pas bloqué un vendredi soir. */
const TYPES_CONTENANT = [
  ['gobelet',         'Gobelet'],
  ['gobelet_25',      'Gobelet 25cl'],
  ['gobelet_50',      'Gobelet 50cl'],
  ['verre_vin',       'Verre à vin'],
  ['verre_champagne', 'Verre à champagne'],
  ['pichet',          'Pichet'],
  ['autre',           'Autre contenant'],
];
const LIBELLE_CONTENANT = Object.fromEntries(TYPES_CONTENANT);

const COLONNES_PRODUIT = [
  {
    sens: 'consigne',
    titre: 'Consignes',
    ajouter: 'Ajouter une consigne',
    vide: 'Aucune consigne. Sans elles, aucune vente ne fera scanner de gobelets.',
  },
  {
    sens: 'deconsigne',
    titre: 'Retours de consigne',
    ajouter: 'Ajouter un retour',
    vide: "Aucun retour déclaré. S'ils existent chez Festicash sans figurer ici, "
        + 'ils seront pris pour des gobelets à scanner.',
  },
];

/* ── Journal des rejets ────────────────────────────────────────────
   Ce que la console montre de la surveillance. Rien ne part vers
   l'extérieur : ni courriel, ni SMS. On enregistre, on affiche.

   Trois lectures, dans cet ordre d'utilité :
     · combien, et de quelle nature — l'état général en un coup d'œil ;
     · QUELLES ADRESSES insistent — c'est la seule information sur
       laquelle on peut agir (bannir, prévenir l'hébergeur) ;
     · le détail chronologique, pour comprendre après coup.

   Un sondage de robot est le bruit de fond normal d'une machine
   exposée : la page ne crie donc pas au rouge pour trois 404. Seules
   les connexions refusées et les cadences bloquées sont colorées —
   elles visent un compte, ce qui n'a rien d'automatique. */
function Securite() {
  const [donnees, setDonnees] = useState(null);
  const [heures, setHeures] = useState(24);
  const [msg, setMsg] = useState({ texte: '', type: '' });

  const charger = async (h) => {
    try {
      setDonnees((await api.get(`/admin/securite?heures=${h}`)).data);
      setMsg({ texte: '', type: '' });
    } catch (e) {
      setMsg({ texte: e.response?.data?.detail || 'Journal indisponible', type: 'erreur' });
    }
  };
  useEffect(() => { charger(heures); }, [heures]);

  const quand = (v) => (v ? new Date(v).toLocaleString('fr-FR',
    { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
  /* Un compte visé, c'est quelqu'un qui cherche à entrer. Un chemin
     inexistant, c'est un robot qui passe. Les deux méritent d'être
     notés, un seul mérite d'attirer l'œil. */
  const grave = (type) => type === 'connexion_refusee' || type === 'trop_de_tentatives';
  const nom = (type) => (donnees?.libelles || {})[type] || type;

  return (
    <Section titre="Journal des rejets">
      <p style={s.hint}>
        Tous les appels <strong>refusés</strong> par JUMS : connexions ratées,
        signatures partenaire invalides, chemins inexistants. Rien n'est envoyé
        à l'extérieur — cette page est le seul endroit où ça se regarde.
        Conservé {donnees?.retention_jours ?? 30} jours.
      </p>

      <div style={{ display: 'flex', gap: espace.sm, marginBottom: espace.lg }}>
        {[[24, '24 heures'], [24 * 7, '7 jours'], [24 * 30, '30 jours']].map(([h, libelle]) => (
          <button key={h} onClick={() => setHeures(h)}
            style={{ ...bouton(heures === h ? 'primaire' : 'discret', 'sm') }}>
            {libelle}
          </button>
        ))}
      </div>

      <Message texte={msg.texte} type={msg.type} />

      {donnees && donnees.total === 0 && (
        <p style={s.vide}>
          Aucun rejet sur la période. C'est le bon résultat — et si le journal
          vient d'être mis en service, c'est aussi le résultat attendu.
        </p>
      )}

      {donnees && donnees.total > 0 && (
        <>
          <div style={{ ...s.deuxColonnes, marginBottom: espace.xl }}>
            <div style={s.colonneLarge}>
              <div style={s.colonneTitre}>Par nature</div>
              <div style={{ marginTop: espace.md }}>
                {donnees.par_type.map(r => (
                  <div key={r.type} style={s.carteLigne}>
                    <span style={s.carteLabel}>
                      {nom(r.type)}
                      {grave(r.type) && <span style={badge('alerte')}>compte visé</span>}
                    </span>
                    <span style={s.carteValeur}>
                      {r.total} · {r.adresses} adresse{r.adresses > 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div style={s.colonneLarge}>
              <div style={s.colonneTitre}>Adresses les plus insistantes</div>
              <div style={s.colonneSous}>
                La seule information sur laquelle on peut agir.
              </div>
              <div style={{ marginTop: espace.md }}>
                {donnees.adresses.length === 0 && (
                  <p style={s.colonneVide}>Aucune adresse identifiée.</p>
                )}
                {donnees.adresses.slice(0, 10).map(a => (
                  <div key={a.adresse} style={s.carteLigne}>
                    <span style={badgeMono(a.quoi.some(grave) ? 'danger' : 'neutre')}>
                      {a.adresse}
                    </span>
                    <span style={s.carteValeur}>
                      {a.total} · dernier {quand(a.dernier)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p style={{ ...texte.etiquette, margin: `0 0 ${espace.sm}px` }}>
            Détail — {donnees.recents.length} entrée(s) les plus récentes
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableau.table}>
              <thead>
                <tr>
                  <th style={s.th}>Quand</th>
                  <th style={s.th}>Nature</th>
                  <th style={s.th}>Adresse</th>
                  <th style={s.th}>Détail</th>
                  <th style={s.th}>Nombre</th>
                </tr>
              </thead>
              <tbody>
                {donnees.recents.map((r, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? couleurs.surface : couleurs.surfaceDouce }}>
                    <td style={s.td}>{quand(r.dernier_at)}</td>
                    <td style={s.td}>
                      <span style={badge(grave(r.type) ? 'alerte' : 'neutre')}>{nom(r.type)}</span>
                    </td>
                    <td style={s.td}>
                      {r.adresse ? <span style={badgeMono()}>{r.adresse}</span>
                                 : <span style={{ color: couleurs.encreFaible }}>—</span>}
                    </td>
                    <td style={{ ...s.td, fontSize: 12, color: couleurs.encreDouce }}>
                      {r.detail || '—'}
                    </td>
                    <td style={{ ...s.td, fontVariantNumeric: 'tabular-nums' }}>
                      {r.occurrences}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Section>
  );
}

function Produits({ festival }) {
  const [produits, setProduits]   = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [typeContenant, setTypeContenant] = useState('gobelet');
  const [nombreVerres, setNombreVerres] = useState('1');
  const [prix, setPrix]           = useState('');
  const [partenaireProductId, setPartenaireProductId] = useState('');
  const [sens, setSens]           = useState('consigne');
  const [msg, setMsg]             = useState({ texte: '', type: '' });

  const charger = async () => {
    try { setProduits((await api.get(`/admin/produits/${festival.id}`)).data); } catch (e) { console.error(e); }
  };
  useEffect(() => { charger(); }, [festival]);

  // `sens` par défaut : celui de la colonne où l'on a cliqué. Les
  // produits d'avant rev28 n'ont pas le champ — ils sont des consignes,
  // c'est ce que dit la valeur par défaut de la colonne en base.
  const ouvrir = (p = null, sensParDefaut = 'consigne') => {
    setEditing(p);
    setTypeContenant(p?.type_contenant || 'gobelet');
    setNombreVerres(String(p?.nombre_verres || 1));
    setPrix(p?.prix != null ? String(p.prix) : '');
    setPartenaireProductId(p?.partenaire_product_id || '');
    setSens(p?.sens || sensParDefaut);
    setMsg({ texte: '', type: '' });
    setShowModal(true);
  };

  const sauvegarder = async () => {
    if (!partenaireProductId) { setMsg({ texte: 'Identifiant produit partenaire obligatoire', type: 'erreur' }); return; }
    if (prix === '' || isNaN(parseFloat(prix)) || parseFloat(prix) < 0) {
      setMsg({ texte: 'Prix invalide', type: 'erreur' }); return;
    }
    try {
      const payload = {
        nombre_verres: parseInt(nombreVerres),
        prix: parseFloat(prix),
        festival_id: festival.id,
        partenaire_product_id: partenaireProductId,
        sens,
        type_contenant: typeContenant,
      };
      if (editing) {
        await api.put(`/admin/produits/${editing.id}`, payload);
      } else {
        await api.post('/admin/produits', payload);
      }
      setMsg({ texte: editing ? 'Produit modifié !' : 'Produit créé !', type: 'succes' });
      charger();
      setTimeout(() => setShowModal(false), 800);
    } catch (e) { setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' }); }
  };

  const supprimer = async (id) => {
    try { await api.delete(`/admin/produits/${id}`); charger(); }
    catch (e) { setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' }); }
  };

  return (
    <>
      <Section titre={`Produits — ${festival.nom}`}>
        <p style={s.hint}>
          Le catalogue des produits Festicash de ce festival. À gauche ce qui
          est <strong>encaissé</strong> au client et fait scanner des gobelets ;
          à droite les <strong>retours de consigne</strong>, que JUMS doit
          reconnaître pour ne surtout pas les compter comme des gobelets à
          rendre. L'identifiant partenaire est ce qui relie les deux mondes.
        </p>
        <div style={s.deuxColonnes}>
          {COLONNES_PRODUIT.map(col => {
            /* Les produits d'avant rev28 n'ont pas de `sens` en base :
               ils valent 'consigne', exactement comme le défaut de la
               colonne. Sans ce repli, ils disparaîtraient des deux
               colonnes le temps qu'un déploiement se termine. */
            const liste = produits.filter(p => (p.sens || 'consigne') === col.sens);
            return (
              <div key={col.sens} style={s.colonneLarge}>
                <div style={s.colonneEntete}>
                  <div style={{ minWidth: 0 }}>
                    <div style={s.colonneTitre}>{col.titre}</div>
                    <div style={s.colonneSous}>
                      {liste.length === 0 ? 'aucun produit'
                        : `${liste.length} produit${liste.length > 1 ? 's' : ''}`}
                    </div>
                  </div>
                </div>

                {liste.length === 0 && <p style={s.colonneVide}>{col.vide}</p>}

                {liste.map(p => (
                  <div key={p.id} style={s.carteLecteur}>
                    <div style={s.festivalItem}>
                      <div style={{ minWidth: 0 }}>
                        <strong>{p.nom}</strong>
                        <span style={badge('succes')}>
                          {p.prix != null ? `${parseFloat(p.prix).toFixed(2)} €` : '— €'}
                        </span>
                        {col.sens === 'consigne' && (
                          <span style={badge('accent')}>{p.nombre_verres} verre(s)</span>
                        )}
                        {p.type_contenant === 'pichet' && (
                          <span style={badge('alerte')}>pichet</span>
                        )}
                        {/* Badge « N puces » retiré le 31/08/2026 à la demande
                            de l'utilisateur : un gobelet EST une puce, un EPC,
                            une ligne. Compter les « puces » d'un produit ne
                            voulait rien dire de plus que compter ses gobelets,
                            et encombrait un écran qui doit rester une simple
                            liste de produits. */}
                        {p.partenaire_product_id
                          ? <span style={badgeMono()}>ID: {p.partenaire_product_id}</span>
                          /* Sans identifiant, ce produit n'est jamais
                             reconnu à l'arrivée d'une vente : il ne sert
                             à rien, et le silence serait trompeur. */
                          : <span style={badge('danger')}>sans identifiant</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <BoutonSm label="Modifier" onClick={() => ouvrir(p, col.sens)} />
                        <BoutonSupprimer onClick={() => supprimer(p.id)} />
                      </div>
                    </div>
                  </div>
                ))}

                <button style={s.colonneAjout} onClick={() => ouvrir(null, col.sens)}>
                  {col.ajouter}
                </button>
              </div>
            );
          })}
        </div>
      </Section>

      {showModal && (
        <Modal titre={editing ? 'Modifier le produit' : 'Nouveau produit'} onClose={() => setShowModal(false)}>
          <div style={s.formulaire}>
            <div>
              <p style={{ ...texte.etiquette, margin: '0 0 4px' }}>Sens *</p>
              <select style={s.input} value={sens} onChange={e => setSens(e.target.value)}>
                <option value="consigne">Consigne — encaissée, fait scanner des gobelets</option>
                <option value="deconsigne">Retour de consigne — à reconnaître et écarter</option>
              </select>
            </div>
            <div>
              <p style={{ ...texte.etiquette, margin: '0 0 4px' }}>Contenant *</p>
              <select style={s.input} value={typeContenant}
                      onChange={e => setTypeContenant(e.target.value)}>
                {TYPES_CONTENANT.map(([code, libelle]) => (
                  <option key={code} value={code}>{libelle}</option>
                ))}
              </select>
            </div>
            <Champ label="Prix (€) *" value={prix} onChange={setPrix} type="number" placeholder="Ex: 4.00" />
            {/* Le nombre de verres n'a de sens que pour une consigne : un
                retour ne fait scanner aucun gobelet, la question ne se
                pose pas. Laisser le champ visible inviterait à y répondre,
                et une valeur saisie là ne servirait jamais à rien. */}
            {sens === 'consigne' && (
              <Champ label="Nombre de verres *" value={nombreVerres} onChange={setNombreVerres} type="number" />
            )}
            <Champ label="ID produit partenaire *" value={partenaireProductId} onChange={setPartenaireProductId} placeholder="Ex: PROD-001" />

            {/* rev25 — les deux champs qui font le lien avec le parc de
                puces. Laissés vides, le produit accepte n'importe quel
                contenant et la consigne du festival s'applique : c'est le
                comportement d'avant, et il reste la valeur par défaut. */}
            {/* Ni « contenant », ni « consigne » ici (retirés le
                31/08/2026) : le NOM du produit EST le contenant, et son
                PRIX est le montant de la consigne. Deux champs de plus
                n'auraient servi qu'à créer des désaccords avec ceux qui
                existent déjà juste au-dessus. */}
            <p style={{ ...s.hint, marginTop: -4 }}>
              {sens === 'consigne' ? (
                <>
                  Le <strong>nom</strong> est aussi le contenant : c'est lui qui
                  apparaît dans le menu d'import de l'onglet Gobelets, et c'est à
                  lui que le scan compare la puce posée. Le <strong>prix</strong>
                  {' '}est le montant de la consigne de ce produit.
                </>
              ) : (
                <>
                  Un retour ne fait scanner aucun gobelet et n'apparaît pas dans
                  l'onglet Gobelets. Seul son <strong>identifiant partenaire</strong>
                  {' '}compte vraiment : c'est à lui que JUMS reconnaîtra une ligne
                  de retour dans une vente, pour l'écarter au lieu de réclamer un
                  gobelet de plus au barman.
                </>
              )}
            </p>

            <Bouton label={editing ? 'Enregistrer les modifications' : 'Créer le produit'} onClick={sauvegarder} />
            <Message texte={msg.texte} type={msg.type} />
          </div>
        </Modal>
      )}
    </>
  );
}


// ─── VERRES ────────────────────────────────────────────────

/* ── Cache de la liste des gobelets ────────────────────────────────
   AU NIVEAU DU MODULE, et c'est essentiel : `Verres` est démonté dès
   qu'on quitte l'onglet, `Admin` dès qu'on va au dashboard. Déclaré
   DANS le composant, ce cache serait recréé à chaque affichage — donc
   inutile — et, pire, il était lu par le premier `useState` bien avant
   sa ligne de déclaration : JavaScript lève alors « Cannot access
   before initialization » et toute la page blanchit.

   C'est exactement ce qui s'est produit le 01/09/2026. Le build ne
   pouvait rien signaler : la syntaxe était valable, l'erreur ne se
   produit qu'à l'exécution.

   Le cache vit le temps de l'onglet ouvert dans le navigateur : un vrai
   rechargement de page repart d'une liste propre, ce qui est le
   comportement attendu quand on force un rafraîchissement. */
const cacheGobelets = new Map();   // festival_id -> tableau de gobelets

/* Valeurs réellement présentes dans une colonne, avec leur effectif.
   Une seule fonction pour les trois filtres — trois versions
   divergeraient à la première évolution. */
function valeursPresentes(lignes, champ) {
  const compte = new Map();
  for (const l of lignes) {
    const v = (l[champ] || '').trim() || null;
    compte.set(v, (compte.get(v) || 0) + 1);
  }
  return [...compte.entries()]
    .sort((a, b) => (a[0] === null ? 1 : b[0] === null ? -1
                     : a[0].localeCompare(b[0], 'fr')))
    .map(([valeur, n]) => ({ valeur, n }));
}

function Verres({ festival }) {
  // Le cache donne l'affichage IMMÉDIAT ; la requête qui suit ne fait
  // que corriger ce qui aurait changé entre-temps.
  const [verres, setVerres]           = useState(() => cacheGobelets.get(festival.id) || []);
  // Deux états distincts, et la distinction compte :
  //   `chargement`   : on n'a RIEN à montrer — premier passage.
  //   `actualisation`: on montre le cache et on vérifie derrière.
  const [chargement, setChargement]     = useState(!cacheGobelets.has(festival.id));
  const [actualisation, setActualisation] = useState(false);
  const [rfidInput, setRfidInput]     = useState('');
  // Plus de valeur par défaut ('50cl' auparavant) : elle enregistrait
  // silencieusement une consigne qui n'existait dans aucun produit.
  const [typeInput, setTypeInput]     = useState('');
  const [modeleInput, setModeleInput] = useState('');
  /* Trois axes, parce que le tableau en a trois — et parce que TOUTES
     les actions de masse dépendent du troisième. Supprimer ne vaut que
     pour le stock, remettre en stock que pour la circulation : sans ce
     filtre, on coche tout et l'action en écarte la moitié sans prévenir.
     C'est la cause des allers-retours de la journée. (31/08/2026) */
  const [filtreConsigne, setFiltreConsigne] = useState('tous');
  const [filtreModele, setFiltreModele]     = useState('tous');
  const [filtreEtat, setFiltreEtat]         = useState('tous');
  const [msg, setMsg]                 = useState({ texte: '', type: '' });
  const [importLoading, setImportLoading] = useState(false);
  // Contenant appliqué à tout un fichier d'import, et conflits rapportés
  // par le dernier import. (rev25)
  const [typeImport, setTypeImport]   = useState('');
  // Le modèle physique — pour les statistiques uniquement. C'est lui qui
  // sépare un 25cl d'un 50cl quand ils partagent produit et consigne.
  const [modeleImport, setModeleImport] = useState('');
  const [conflits, setConflits]       = useState([]);
  // Les contenants viennent des PRODUITS, jamais du nom du fichier ni
  // d'une saisie libre : c'est le seul moyen qu'une puce importée puisse
  // effectivement satisfaire une vente.
  const [contenantsProduits, setContenantsProduits] = useState([]);
  const fileInputRef = React.useRef(null);

  // ── Remise en stock manuelle (rev17) ──────────────────────
  // Le choix « avec / sans remboursement » est demandé À CHAQUE FOIS :
  // les deux cas existent réellement (gobelet mal associé à corriger vs
  // gobelet réellement perdu). L'action TOTALE exige en plus le mot de
  // passe du compte connecté.
  const [selection, setSelection]     = useState([]);
  const [modalAction, setModalAction] = useState(null);   // { portee, uids }
  // Suppression en lot (rev25) : la liste d'EPC en attente de confirmation.
  const [suppressionLot, setSuppressionLot] = useState(null);
  // Suppression TOTALE : protégée par le mot de passe du compte.
  const [suppressionTotale, setSuppressionTotale] = useState(false);
  const [mdpSuppression, setMdpSuppression] = useState('');
  const [motDePasse, setMotDePasse]   = useState('');
  const [motif, setMotif]             = useState('');
  const [enCours, setEnCours]         = useState(false);
  const [msgModal, setMsgModal]       = useState({ texte: '', type: '' });
  const [historique, setHistorique]   = useState([]);
  const [showHisto, setShowHisto]     = useState(false);
  const [detailLot, setDetailLot]     = useState(null);   // { lot, lignes }

  /* `TYPES_VERRES` — liste écrite en dur ('25cl', '33cl', '50cl'…) —
   supprimée le 31/08/2026.

   Elle filtrait sur la colonne CONSIGNE tout en proposant des volumes :
   depuis que cette colonne porte le nom du produit (« consigne
   gobelet »), le menu ne pouvait plus rien trouver. Il ne s'agissait pas
   d'une liste mal alimentée mais d'un filtre cassé.

   Elle servait aussi au formulaire d'ajout manuel — on y choisissait
   donc un volume dans un champ qui attend une consigne, ce qui créait
   des gobelets qu'aucun produit ne pouvait satisfaire.

   Tout vient désormais des données réelles : consignes des produits,
   modèles saisis à l'import. Rien n'est plus décidé dans le code. */

  const charger = async () => {
    // Premier passage sur ce festival : rien à montrer, on le dit.
    // Sinon on garde le cache à l'écran et on actualise discrètement —
    // l'utilisateur ne doit jamais voir sa liste redevenir vide.
    if (cacheGobelets.has(festival.id)) setActualisation(true);
    else setChargement(true);
    try {
      const lignes = (await api.get(`/admin/verres/liste/${festival.id}`)).data;
      cacheGobelets.set(festival.id, lignes);
      setVerres(lignes);
    } catch (e) {
      console.error(e);
      // Une erreur ne doit pas effacer ce qui est déjà affiché : le cache
      // périmé vaut mieux qu'un écran vide qui ment.
      setMsg({ texte: "Liste des gobelets non actualisée — le serveur n'a pas répondu. "
                      + "Les gobelets affichés peuvent dater de quelques instants.",
               type: 'erreur' });
    } finally {
      setChargement(false); setActualisation(false);
    }
    // Les NOMS des produits consignés — c'est eux, le contenant. Pas de
    // champ séparé à tenir en accord : l'onglet Produits liste déjà
    // « Pichet », « Gobelet 25cl »…
    try {
      const produits = (await api.get(`/admin/produits/${festival.id}`)).data;
      // Les CONSIGNES seulement : un produit de retour n'est pas un
      // contenant qu'on puisse coller sur une puce, et le proposer ici
      // reviendrait à créer des gobelets estampillés « déconsigne ».
      setContenantsProduits(
        [...new Set(produits.filter(p => (p.sens || 'consigne') === 'consigne')
                            .map(p => (p.nom || '').trim()).filter(Boolean))].sort());
    } catch (e) { setContenantsProduits([]); }
  };
  const chargerHistorique = async () => {
    try { setHistorique((await api.get(`/admin/verres/remises-en-stock/${festival.id}`)).data); }
    catch (e) { console.error(e); }
  };
  useEffect(() => {
    // Changement de festival : on repart de SON cache, pas de celui du
    // précédent — sinon on afficherait un instant les gobelets d'un autre
    // festival, ce qui serait bien pire qu'un écran vide.
    const enCache = cacheGobelets.get(festival.id);
    setVerres(enCache || []);
    setChargement(!enCache);
    charger(); chargerHistorique(); setSelection([]);
  }, [festival]);

  const enregistrer = async () => {
    if (!rfidInput.trim()) return;
    try {
      const res = await api.post('/admin/verres/enregistrer', {
        rfid_uids: [rfidInput.trim().toUpperCase()],
        festival_id: festival.id,
        type_verre: typeInput || null,
        modele: modeleInput.trim() || null,
      });
      // `bracelets` : l'EPC est déjà déclaré comme puce de bracelet
      // barman — le dire explicitement, sinon « déjà enregistré »
      // enverrait chercher un gobelet qui n'existe pas.
      setMsg(res.data.bracelets
        ? { texte: `${rfidInput} est une puce de BRACELET BARMAN — non enregistrée comme gobelet`, type: 'erreur' }
        : { texte: res.data.inseres === 1
              ? `Gobelet ${rfidInput} enregistré`
                + (typeInput ? ` — ${typeInput}` : ' — sans consigne')
                + (modeleInput.trim() ? ` (${modeleInput.trim()})` : '')
              : `Gobelet ${rfidInput} déjà enregistré`,
            type: res.data.inseres === 1 ? 'succes' : 'erreur' });
      setRfidInput(''); charger();
    } catch (e) { setMsg({ texte: "Erreur", type: 'erreur' }); }
  };

  const supprimer = async (rfid_uid) => {
    // Le festival est indispensable : le même gobelet peut être inscrit
    // dans plusieurs, et on ne retire que SON inscription ici.
    try { await api.delete(`/admin/verres/${rfid_uid}?festival_id=${festival.id}`); charger(); }
    catch (e) { setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' }); }
  };

  const basculerSelection = (uid) => setSelection(sel =>
    sel.includes(uid) ? sel.filter(u => u !== uid) : [...sel, uid]);

  /* Suppression en lot (rev25). Un seul appel plutôt que N : supprimer
     deux cents gobelets un par un depuis le navigateur, c'est deux cents
     allers-retours et un écran figé. */
  const supprimerLot = async () => {
    if (!suppressionLot?.length) return;
    try {
      const res = await api.post('/admin/verres/supprimer',
        { festival_id: festival.id, rfid_uids: suppressionLot });
      if (res.data.supprimes === 0) {
        setMsgModal({
          texte: res.data.ignores_en_circulation > 0
            ? `Aucun gobelet supprimé : les ${res.data.ignores_en_circulation} sélectionnés sont encore EN CIRCULATION. Remettez-les d'abord en stock.`
            : 'Aucun gobelet supprimé — la sélection ne correspond à rien dans ce festival.',
          type: 'erreur',
        });
        charger();
        return;
      }
      setMsg({ texte: res.data.message, type: 'succes' });
      setSuppressionLot(null); setSelection([]);
      charger();
    } catch (e) {
      setMsgModal({ texte: e.response?.data?.detail || 'Suppression impossible', type: 'erreur' });
    }
  };

  const fermerModal = () => {
    setModalAction(null); setMotDePasse(''); setMotif('');
    setMsgModal({ texte: '', type: '' });
  };

  /* Un seul appel pour les deux cas — c'est le booléen `rembourser` qui
     décide si le client récupère sa consigne ou si elle est conservée.
     Aucune valeur par défaut : le bouton cliqué EST le choix. */
  const executer = async (rembourser) => {
    if (!modalAction || enCours) return;
    setEnCours(true);
    setMsgModal({ texte: '', type: '' });
    try {
      const totale = modalAction.portee === 'totale';
      const res = await api.post(
        totale ? '/admin/verres/remise-en-stock-totale' : '/admin/verres/remise-en-stock',
        totale
          ? { festival_id: festival.id, rembourser, mot_de_passe: motDePasse, motif: motif || null }
          : { festival_id: festival.id, rfid_uids: modalAction.uids, rembourser, motif: motif || null }
      );
      setMsg({ texte: res.data.message, type: 'succes' });
      fermerModal(); setSelection([]);
      charger(); chargerHistorique();
    } catch (e) {
      setMsgModal({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' });
    }
    setEnCours(false);
  };

  const ouvrirDetailLot = async (lot) => {
    try {
      const lignes = (await api.get(`/admin/verres/remises-en-stock/lot/${lot.lot_id}`)).data;
      setDetailLot({ lot, lignes });
    } catch (e) { setMsg({ texte: 'Détail indisponible', type: 'erreur' }); }
  };

  const importerExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportLoading(true);
    setMsg({ texte: '', type: '' });
    setConflits([]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Le type du contenant s'applique à TOUT le fichier — le geste
      // réel est « voici le fichier des pichets ». Une colonne de type
      // dans le fichier reste prioritaire, ligne par ligne.
      if (typeImport.trim()) formData.append('type_verre', typeImport.trim());
      if (modeleImport.trim()) formData.append('modele', modeleImport.trim());
      const res = await api.post(`/admin/verres/import-excel?festival_id=${festival.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const d = res.data;
      const parts = [`${d.inseres} verre(s) importé(s)`];
      if (d.types_completes > 0) parts.push(`${d.types_completes} type(s) complété(s)`);
      parts.push(`${d.doublons} doublon(s) inchangé(s)`);
      if (d.erreurs > 0) parts.push(`${d.erreurs} erreur(s)`);
      if (d.bracelets > 0) parts.push(`${d.bracelets} puce(s) de bracelet écartée(s)`);
      if (d.conflits > 0) parts.push(`${d.conflits} conflit(s) à trancher`);
      // Une colonne de type dans le fichier n'est plus prioritaire : le
      // dire, sinon on croit qu'elle a servi. C'est ce silence qui a fait
      // entrer une liste de 25cl sous « 25cl » au lieu de la consigne
      // choisie (31/08/2026).
      if (d.colonne_ignoree) {
        parts.push(`colonne « ${d.colonne_ignoree} » du fichier ignorée — votre choix prime`);
      }
      setMsg({ texte: parts.join(' — '), type: d.conflits > 0 ? 'alerte' : 'succes' });
      setConflits(d.detail_conflits || []);
      charger();
    } catch (e) {
      setMsg({ texte: e.response?.data?.detail || "Erreur lors de l'import", type: 'erreur' });
    }
    setImportLoading(false);
    e.target.value = '';
  };

  /* Trancher un conflit : la puce était déjà connue sous un autre type.
     On n'écrase JAMAIS à l'import — ici c'est un geste explicite de
     l'utilisateur, donc l'écrasement est légitime. */
  const trancherConflit = async (rfid_uid, type_verre) => {
    try {
      await api.post('/admin/verres/type',
        { festival_id: festival.id, rfid_uids: [rfid_uid], type_verre });
      setConflits(cs => cs.filter(c => c.rfid_uid !== rfid_uid));
      charger();
    } catch (e) {
      setMsg({ texte: e.response?.data?.detail || 'Modification impossible', type: 'erreur' });
    }
  };

  // Écarter = retirer de la liste des conflits. La puce garde son type
  // actuel, rien n'est effacé en base.
  const ecarterConflit = (rfid_uid) =>
    setConflits(cs => cs.filter(c => c.rfid_uid !== rfid_uid));

  const supprimerTout = async () => {
    try {
      const res = await api.post('/admin/verres/supprimer',
        { festival_id: festival.id, tout: true, mot_de_passe: mdpSuppression });
      /* Une action qui ne retire RIEN ne doit pas se refermer comme si
         elle avait réussi — c'est ce qui donnait « impossible de
         supprimer, rien ne se passe » (constat du 31/08/2026). On garde
         la fenêtre ouverte et on dit pourquoi. */
      if (res.data.supprimes === 0) {
        setMsgModal({
          texte: res.data.ignores_en_circulation > 0
            ? `Aucun gobelet supprimé : les ${res.data.ignores_en_circulation} gobelets de ce festival sont encore EN CIRCULATION. Leur consigne est due à un client — remettez-les d'abord en stock, puis relancez la suppression.`
            : "Aucun gobelet à supprimer : ce festival n'en contient aucun.",
          type: 'erreur',
        });
        charger();
        return;
      }
      setMsg({ texte: res.data.message, type: 'succes' });
      setSuppressionTotale(false); setMdpSuppression(''); setSelection([]);
      charger();
    } catch (e) {
      setMsgModal({ texte: e.response?.data?.detail || 'Suppression impossible',
                    type: 'erreur' });
    }
  };

  const etatCouleur = (etat) => (etat === 'stock' ? badgeStyle('succes') : etat === 'en_circulation' ? badgeStyle('accent') : badgeStyle('neutre'));

  /* Les trois filtres se COMBINENT. `'tous'` laisse passer, `'(vide)'`
     ne retient que les gobelets dont la valeur n'est pas renseignée —
     c'est précisément ceux-là qu'on cherche quand on répare un parc. */
  const passe = (valeur, filtre) => {
    if (filtre === 'tous') return true;
    const v = (valeur || '').trim() || null;
    return filtre === '(vide)' ? v === null : v === filtre;
  };
  const verresFiltres = verres.filter(v =>
    passe(v.type_verre, filtreConsigne)
    && passe(v.modele, filtreModele)
    && (filtreEtat === 'tous' || v.etat === filtreEtat));

  /* Les deux menus n'ont PAS la même source, et c'est le fond du sujet.

     CONSIGNE  ← les produits de l'onglet Produits. C'est la liste de
       référence : elle existe même si aucun gobelet ne la porte encore,
       et c'est justement ce qu'on veut voir. La lire sur les gobelets
       était l'erreur — un parc mal étiqueté faisait alors apparaître des
       volumes (« 25cl ») dans le menu des consignes, et les deux menus
       finissaient par se ressembler. (31/08/2026)

     MODÈLE    ← ce qui a été saisi à la main sur les gobelets. Là il n'y
       a pas de liste de référence : le modèle est libre par nature.

     L'effectif affiché reste celui du parc dans les deux cas — c'est le
     nombre de gobelets concernés qui intéresse, pas le nombre d'options. */
  const compteParConsigne = valeursPresentes(verres, 'type_verre')
    .reduce((acc, o) => { acc[o.valeur ?? '(vide)'] = o.n; return acc; }, {});
  const consignesProduits = contenantsProduits.map(nom => ({
    valeur: nom, n: compteParConsigne[nom] || 0,
  }));
  // Valeurs portées par des gobelets mais qui ne correspondent à aucun
  // produit : ce sont les gobelets à réparer. Les cacher reviendrait à
  // les rendre introuvables.
  const consignesOrphelines = Object.keys(compteParConsigne)
    .filter(v => v !== '(vide)' && !contenantsProduits.includes(v))
    .sort((a, b) => a.localeCompare(b, 'fr'))
    .map(v => ({ valeur: v, n: compteParConsigne[v] }));
  const sansConsigne = compteParConsigne['(vide)'] || 0;

  const modelesPresents = valeursPresentes(verres, 'modele');
  const nbEnCirculation = verres.filter(v => v.etat === 'en_circulation').length;
  // Ce qui peut réellement être supprimé : un gobelet en circulation ne
  // l'est jamais, sa consigne est due à un client.
  const nbSupprimables = verres.length - nbEnCirculation;
  /* « Tout cocher » porte sur TOUS les gobelets du filtre courant, pas
     sur les 200 dessinés (constat utilisateur du 31/08/2026 : « il en
     sélectionne que 200 »). Le tableau n'en affiche que 200 pour rester
     fluide, mais la sélection ne doit pas hériter de cette limite
     d'affichage — elle n'a rien à voir avec l'intention de l'utilisateur.
     La liste complète est déjà en mémoire : l'API ne tronque pas. */
  const affiches = verresFiltres.map(v => v.rfid_uid);
  const toutAfficheSelectionne = affiches.length > 0
    && affiches.every(u => selection.includes(u));
  /* Ce que la sélection contient réellement — les deux actions n'ont pas
     le même domaine, et l'écran doit le dire AVANT le clic, pas après. */
  const parUid = Object.fromEntries(verres.map(v => [v.rfid_uid, v]));
  const selEnCirculation = selection.filter(u => parUid[u]?.etat === 'en_circulation');
  const selEnStock = selection.filter(u => parUid[u] && parUid[u].etat !== 'en_circulation');

  return (
    <Section titre={`Gobelets — ${festival.nom}`}>
      <p style={s.hint}>
        Scannez ou tapez l'EPC du gobelet puis Entrée, ou importez un fichier Excel.
        Une puce déjà déclarée comme bracelet de barman est écartée automatiquement.
      </p>

      <div style={s.formulaire}>
        <div style={{ display: 'flex', gap: espace.sm, flexWrap: 'wrap' }}>
          <input style={{ ...s.input, flex: 2, fontFamily: police.mono, minWidth: 220 }} type="text"
            placeholder="EPC UHF — ex. 330943A74AD8060000050822"
            value={rfidInput} onChange={e => setRfidInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && enregistrer()} autoFocus />
          {/* Même logique que l'import : la consigne est une liste
              FERMÉE venue des produits, le modèle une saisie libre. Ce
              champ proposait des volumes ('25cl'…) alors qu'il alimente
              la colonne CONSIGNE — on créait donc à la main des gobelets
              qu'aucun produit ne pouvait satisfaire. */}
          <select style={{ ...s.input, flex: 1, minWidth: 170 }}
                  value={typeInput} onChange={e => setTypeInput(e.target.value)}>
            <option value="">— Consigne —</option>
            {contenantsProduits.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <input style={{ ...s.input, flex: 1, minWidth: 130 }}
                 placeholder="Modèle (ex : 25cl)"
                 value={modeleInput} onChange={e => setModeleInput(e.target.value)} />
          <Bouton label="Enregistrer" onClick={enregistrer} />
        </div>
        <Message texte={msg.texte} type={msg.type} />
      </div>

      {/* Import en masse : une simple zone bordée, plus un encadré bleu
          en pointillés. Ce n'est pas une alerte, c'est un outil. */}
      <div style={{ marginBottom: espace.xl, padding: espace.lg,
                    border: `1px solid ${couleurs.bord}`, borderRadius: rayon.lg }}>
        <p style={{ ...texte.etiquette, margin: `0 0 ${espace.sm}px` }}>Import en masse</p>
        <p style={{ ...texte.aide, margin: `0 0 ${espace.md}px` }}>
          Fichier Excel avec une colonne <code>uid</code> (ou rfid_uid / epc).
          Rien dans un fichier ne dit ce qu'il contient : les deux champs
          ci-dessous s'appliquent à toutes ses puces.
          <br />
          <strong>Consigne</strong> — le produit consigné, choisi dans la liste. C'est lui que le scan
          vérifie : une puce « Gobelet » ne peut pas remplir une ligne
          « Pichet ».
          <br />
          <strong>Modèle</strong> — le volume réel, <em>25cl</em>,{' '}
          <em>50cl</em>… Facultatif, et sans aucun effet sur le scan : il ne
          sert qu'à séparer vos statistiques. C'est le seul moyen de distinguer
          deux volumes qui partagent le même produit et la même consigne.
        </p>
        <div style={{ display: 'flex', gap: espace.sm, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Menu fermé, alimenté par les contenants déclarés dans
              l'onglet Produits. Une saisie libre ici créerait des
              contenants (« Pichet », « pichet ») que plus aucun produit
              ne reconnaîtrait — donc des puces qu'aucune vente ne pourrait
              consommer. Le seul endroit où l'on invente un contenant est
              l'onglet Produits. */}
          <select value={typeImport} onChange={e => setTypeImport(e.target.value)}
                  style={{ ...s.input, width: 240 }}>
            <option value="">— Consigne (produit) —</option>
            {contenantsProduits.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {/* Le MODÈLE, libre (rev25). Deux volumes peuvent partager le
              même produit et la même consigne — c'est la seule chose qui
              les sépare ensuite dans les statistiques. Le scan ne le
              regarde jamais. */}
          <input value={modeleImport} onChange={e => setModeleImport(e.target.value)}
                 placeholder="Modèle — ex : gobelet 25cl"
                 list="modeles-connus"
                 style={{ ...s.input, width: 220 }} />
          <datalist id="modeles-connus">
            {[...new Set(verres.map(v => (v.modele || '').trim()).filter(Boolean))]
              .sort().map(m => <option key={m} value={m} />)}
          </datalist>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={importerExcel} style={{ display: 'none' }} />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importLoading || !typeImport}
            style={{ ...bouton('discret'),
                     opacity: (importLoading || !typeImport) ? 0.45 : 1,
                     cursor: importLoading ? 'progress' : (!typeImport ? 'not-allowed' : 'pointer') }}
            title={!typeImport ? 'Choisissez d’abord la consigne' : undefined}
          >
            {importLoading ? 'Import en cours…' : 'Choisir un fichier Excel'}
          </button>
        </div>
        {contenantsProduits.length === 0 && (
          <p style={{ ...texte.aide, margin: `${espace.sm}px 0 0`, color: couleurs.alerte }}>
            <strong>Aucun produit consigné enregistré.</strong> Créez-les
            d'abord dans l'onglet Produits — leurs noms sont les consignes,
            et ce sont eux qui alimentent ce menu.
          </p>
        )}
      </div>

      {/* Conflits du dernier import : une puce déjà connue sous un AUTRE
          type. Jamais écrasée automatiquement — c'est soit une erreur de
          saisie, soit une étiquette recollée, et les deux méritent un œil.
          Rien n'est stocké : réimporter le même fichier reproduit la même
          liste. (rev24) */}
      {conflits.length > 0 && (
        <div style={{ marginBottom: espace.xl, padding: espace.lg,
                      border: `1px solid ${couleurs.alerte}`, borderRadius: rayon.lg,
                      background: couleurs.alerteDoux }}>
          <p style={{ ...texte.etiquette, margin: `0 0 ${espace.sm}px`, color: couleurs.alerte }}>
            {conflits.length} gobelet(s) déjà enregistré(s) sous une autre consigne
          </p>
          <p style={{ ...texte.aide, margin: `0 0 ${espace.md}px` }}>
            Rien n'a été modifié pour ceux-ci. Choisissez la consigne à
            garder, ou écartez la ligne pour laisser la puce telle quelle.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: espace.sm }}>
            {conflits.map(c => (
              <div key={c.rfid_uid} style={{
                display: 'flex', gap: espace.sm, alignItems: 'center', flexWrap: 'wrap',
                padding: espace.sm, background: couleurs.surface, borderRadius: rayon.md,
              }}>
                <code style={{ fontSize: 12.5 }}>{c.rfid_uid}</code>
                <span style={{ ...texte.aide, margin: 0 }}>
                  enregistrée <strong>{c.type_actuel}</strong>, le fichier dit{' '}
                  <strong>{c.type_propose}</strong>
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: espace.sm }}>
                  <button style={bouton('discret')}
                          onClick={() => trancherConflit(c.rfid_uid, c.type_actuel)}>
                    Garder {c.type_actuel}
                  </button>
                  <button style={bouton('primaire')}
                          onClick={() => trancherConflit(c.rfid_uid, c.type_propose)}>
                    Passer en {c.type_propose}
                  </button>
                  <button style={bouton('discret')} onClick={() => ecarterConflit(c.rfid_uid)}>
                    Écarter
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trois filtres, tous construits sur les données réelles. Chaque
          option porte son effectif : on voit ce qu'on va sélectionner
          AVANT de cocher, et donc avant d'agir. */}
      <div style={{ display: 'flex', gap: espace.md, flexWrap: 'wrap',
                    alignItems: 'flex-end', marginBottom: espace.lg }}>
        <div>
          <p style={{ ...texte.etiquette, margin: `0 0 3px` }}>Consigne</p>
          <select style={{ ...s.input, minWidth: 230, marginBottom: 0 }}
                  value={filtreConsigne} onChange={e => setFiltreConsigne(e.target.value)}>
            <option value="tous">Toutes ({verres.length})</option>
            {/* Les produits de l'onglet Produits — la liste de référence,
                même ceux qu'aucun gobelet ne porte encore. */}
            {consignesProduits.map(o => (
              <option key={o.valeur} value={o.valeur}>{o.valeur} ({o.n})</option>
            ))}
            {sansConsigne > 0 && (
              <option value="(vide)">non renseignée ({sansConsigne})</option>
            )}
            {/* Valeurs présentes sur des gobelets mais correspondant à
                aucun produit : ce sont exactement ceux à réparer. */}
            {consignesOrphelines.length > 0 && (
              <optgroup label="À corriger — ne correspond à aucun produit">
                {consignesOrphelines.map(o => (
                  <option key={o.valeur} value={o.valeur}>{o.valeur} ({o.n})</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        <div>
          <p style={{ ...texte.etiquette, margin: `0 0 3px` }}>Modèle</p>
          <select style={{ ...s.input, minWidth: 190, marginBottom: 0 }}
                  value={filtreModele} onChange={e => setFiltreModele(e.target.value)}>
            <option value="tous">Tous ({verres.length})</option>
            {modelesPresents.map(o => (
              <option key={o.valeur ?? '(vide)'} value={o.valeur ?? '(vide)'}>
                {o.valeur ?? 'non renseigné'} ({o.n})
              </option>
            ))}
          </select>
        </div>

        {/* L'axe qui manquait. Toutes les actions de masse en dépendent :
            « en stock » + tout cocher + supprimer, et les trois chiffres
            concordent enfin. */}
        <div>
          <p style={{ ...texte.etiquette, margin: `0 0 3px` }}>État</p>
          <select style={{ ...s.input, minWidth: 190, marginBottom: 0 }}
                  value={filtreEtat} onChange={e => setFiltreEtat(e.target.value)}>
            <option value="tous">Tous ({verres.length})</option>
            <option value="stock">En stock ({verres.length - nbEnCirculation})</option>
            <option value="en_circulation">En circulation ({nbEnCirculation})</option>
          </select>
        </div>

        {(filtreConsigne !== 'tous' || filtreModele !== 'tous' || filtreEtat !== 'tous') && (
          <BoutonSm label="Tout afficher" onClick={() => {
            setFiltreConsigne('tous'); setFiltreModele('tous'); setFiltreEtat('tous');
          }} />
        )}
      </div>

      {/* ── Remise en stock manuelle ───────────────────────────
          Deux portées : une sélection précise (cases à cocher), ou
          TOUS les gobelets en circulation (mot de passe exigé). Dans
          les deux cas le choix « avec / sans remboursement » est
          demandé dans la fenêtre suivante. */}
      <div style={{ marginBottom: espace.xl, padding: espace.lg,
                    border: `1px solid ${couleurs.bord}`,
                    borderLeft: `2px solid ${couleurs.alerte}`, borderRadius: rayon.lg }}>
        <p style={{ ...texte.section, margin: `0 0 ${espace.xs}px` }}>
          Actions sur la sélection
          <span style={badge('neutre')}>{selection.length} sélectionné(s)</span>
          <span style={badge('neutre')}>{nbEnCirculation} en circulation</span>
        </p>
        {/* rev25 — chaque action dit sur COMBIEN de la sélection elle
            porte. Les deux n'ont pas le même domaine : on ne remet pas en
            stock un gobelet déjà en stock, on ne supprime pas un gobelet
            encore en circulation (sa consigne est due à un client). Avant,
            l'écran cachait les commandes au lieu de l'expliquer — et elles
            paraissaient simplement cassées. */}
        <p style={{ ...texte.aide, margin: `0 0 ${espace.md}px` }}>
          <strong>Remettre en stock</strong> ne concerne que les gobelets en
          circulation — à n'utiliser que pour corriger une erreur, le retour
          normal passe par un bac de déconsigne.{' '}
          <strong>Supprimer</strong> ne concerne que ceux déjà en stock : un
          gobelet en circulation doit encore être remboursé, le retirer
          romprait cette chaîne.
        </p>
        <div style={{ display: 'flex', gap: espace.sm, flexWrap: 'wrap', alignItems: 'center' }}>
          <BoutonSm
            label={`Remettre en stock (${selEnCirculation.length})`}
            onClick={() => selEnCirculation.length
              && setModalAction({ portee: 'selection', uids: selEnCirculation })} />
          <BoutonSm
            label={`Supprimer de ce festival (${selEnStock.length})`}
            variante="danger"
            onClick={() => selEnStock.length && setSuppressionLot(selEnStock)} />
          {selection.length > 0 && (
            <BoutonSm label="Vider la sélection" onClick={() => setSelection([])} />
          )}
          <BoutonSm
            label={`Remettre en stock TOUT le festival (${nbEnCirculation})`}
            onClick={() => nbEnCirculation && setModalAction({ portee: 'totale', uids: [] })}
            variante="danger" />
          {/* Suppression totale (rev25). Sans elle, vider un parc de
              9 000 puces obligeait à les envoyer une à une dans la
              requête. Protégée par le mot de passe du compte, comme la
              remise en stock totale : une session admin reste souvent
              ouverte sur un poste partagé. */}
          {/* Le compte annoncé est celui des gobelets RÉELLEMENT
              supprimables — ceux en stock. Afficher le parc entier
              promettait ce que la règle interdit de tenir. */}
          <BoutonSm
            label={`Supprimer TOUT le parc (${nbSupprimables})`}
            onClick={() => nbSupprimables && setSuppressionTotale(true)}
            variante="danger" />
          <BoutonSm
            label={showHisto ? "Masquer l'historique" : `Historique (${historique.length})`}
            onClick={() => setShowHisto(h => !h)} />
        </div>
        {selection.length > 0 && selEnCirculation.length === 0 && selEnStock.length === 0 && (
          <p style={{ ...texte.aide, margin: `${espace.sm}px 0 0`, color: couleurs.alerte }}>
            Les gobelets sélectionnés ne sont plus dans la liste chargée —
            rafraîchissez la page.
          </p>
        )}
      </div>

      {/* Confirmation de suppression en lot (rev25). Geste destructeur :
          il exige un clic délibéré, et annonce le nombre exact. */}
      {suppressionLot && (
        <Modal titre="Supprimer des gobelets de ce festival"
               onClose={() => setSuppressionLot(null)}>
          <p style={s.hint}>
            <strong>{suppressionLot.length} gobelet(s)</strong> vont être retirés
            de la liste de <strong>{festival.nom}</strong>, avec leur historique
            de transactions dans ce festival.
          </p>
          <p style={s.hint}>
            Les puces elles-mêmes ne sont pas détruites : si elles sont inscrites
            dans un autre festival, elles y restent. Vous pourrez les réimporter
            ici plus tard.
          </p>
          <div style={{ display: 'flex', gap: espace.sm, marginTop: espace.md }}>
            <BoutonSm label="Annuler" onClick={() => setSuppressionLot(null)} />
            <BoutonSm label={`Supprimer les ${suppressionLot.length}`} variante="danger"
                      onClick={supprimerLot} />
          </div>
          <Message texte={msgModal.texte} type={msgModal.type} />
        </Modal>
      )}

      {suppressionTotale && (
        <Modal titre="Supprimer TOUT le parc de ce festival"
               onClose={() => { setSuppressionTotale(false); setMdpSuppression(''); }}>
          <p style={s.hint}>
            <strong>{nbSupprimables} gobelet(s) en stock</strong> de{' '}
            <strong>{festival.nom}</strong> seront retirés, avec leur historique
            de transactions dans ce festival.
            {nbEnCirculation > 0 && (
              <> Les <strong>{nbEnCirculation} encore en circulation</strong> ne
              seront pas touchés.</>
            )}
          </p>
          <p style={s.hint}>
            Les gobelets <strong>encore en circulation ne sont pas touchés</strong> :
            leur consigne est due à un client, les retirer romprait la chaîne de
            remboursement. Ils vous seront rapportés.
          </p>
          <p style={s.hint}>
            Les puces ne sont pas détruites : celles inscrites dans un autre
            festival y restent, et vous pourrez les réimporter ici.
          </p>
          <Champ label="Votre mot de passe" value={mdpSuppression}
                 onChange={setMdpSuppression} type="password" />
          <div style={{ display: 'flex', gap: espace.sm, marginTop: espace.md }}>
            <BoutonSm label="Annuler"
                      onClick={() => { setSuppressionTotale(false); setMdpSuppression(''); }} />
            <BoutonSm label="Supprimer tout le parc" variante="danger"
                      onClick={supprimerTout} />
          </div>
          <Message texte={msgModal.texte} type={msgModal.type} />
        </Modal>
      )}

      {showHisto && (
        <div style={{ marginBottom: 20, padding: 16, background: couleurs.surfaceDouce, border: `1px solid ${couleurs.bord}`, borderRadius: 10 }}>
          <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: '600', color: couleurs.encre }}>
            Historique des remises en stock manuelles
          </p>
          {historique.length === 0
            ? <p style={{ fontSize: 12, color: couleurs.encreFaible, margin: 0 }}>Aucune remise en stock manuelle enregistrée.</p>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={s.th}>Date</th>
                    <th style={s.th}>Portée</th>
                    <th style={s.th}>Gobelets</th>
                    <th style={s.th}>Client remboursé ?</th>
                    <th style={s.th}>Montant</th>
                    <th style={s.th}>Par</th>
                    <th style={s.th}>Motif</th>
                    <th style={s.th}></th>
                  </tr></thead>
                  <tbody>
                    {historique.map((h, i) => (
                      <tr key={h.lot_id} style={{ background: i % 2 === 0 ? couleurs.surface : couleurs.surfaceDouce }}>
                        <td style={s.td}>{new Date(h.created_at).toLocaleString('fr-FR')}</td>
                        <td style={s.td}>
                          <span style={badge(h.portee === 'totale' ? 'danger' : 'neutre')}>
                            {h.portee === 'totale' ? 'totale' : 'sélection'}
                          </span>
                        </td>
                        <td style={s.td}><strong>{h.nb_verres}</strong></td>
                        <td style={s.td}>
                          <span style={badge(h.rembourse ? 'succes' : 'danger')}>
                            {h.rembourse ? 'remboursé' : 'consigne conservée'}
                          </span>
                        </td>
                        <td style={s.td}>{h.rembourse ? `${Number(h.montant_total).toFixed(2)} €` : '—'}</td>
                        <td style={{ ...s.td, fontSize: 12 }}>{h.utilisateur_email || '—'}</td>
                        <td style={{ ...s.td, fontSize: 12, color: couleurs.encreDouce }}>{h.motif || '—'}</td>
                        <td style={s.td}><BoutonSm label="Détail" onClick={() => ouvrirDetailLot(h)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {/* « 0 gobelet » et « je n'ai pas encore reçu la liste » ne doivent
          jamais se ressembler. C'est cette confusion qui a fait croire à
          un parc effacé alors que les 5 306 gobelets étaient bien là. */}
      <p style={s.hint}>
        {chargement ? (
          <strong>Chargement des gobelets…</strong>
        ) : (
          <>
            <strong>{verresFiltres.length}</strong> gobelet(s) affiché(s)
            {verresFiltres.length !== verres.length && ` sur ${verres.length} dans ce festival`}
            {actualisation && (
              <span style={{ marginLeft: espace.sm, color: couleurs.encreFaible }}>
                · actualisation…
              </span>
            )}
          </>
        )}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...s.th, width: 34 }}>
              <input type="checkbox" checked={toutAfficheSelectionne}
                disabled={affiches.length === 0}
                title={`Sélectionner les ${affiches.length} gobelet(s) du filtre courant`}
                onChange={() => setSelection(sel => toutAfficheSelectionne
                  ? sel.filter(u => !affiches.includes(u))
                  : [...new Set([...sel, ...affiches])])} />
            </th>
            <th style={s.th}>EPC (rfid_uid)</th>
            <th style={s.th}>Consigne</th>
            <th style={s.th}>Modèle</th>
            <th style={s.th}>État</th>
            <th style={s.th}>Mise à jour</th>
            <th style={s.th}>Action</th>
          </tr></thead>
          <tbody>
            {verresFiltres.slice(0, 200).map((v, i) => (
              <tr key={i} style={{ background: selection.includes(v.rfid_uid) ? couleurs.alerteDoux : (i % 2 === 0 ? couleurs.surface : couleurs.surfaceDouce) }}>
                <td style={s.td}>
                  {/* rev25 — la case existe sur TOUS les gobelets. Elle
                      n'apparaissait que sur ceux en circulation : sur un
                      parc entièrement en stock, la colonne était vide et
                      la sélection semblait cassée. Ce sont les ACTIONS
                      qui trient ensuite, pas la case. */}
                  <input type="checkbox" checked={selection.includes(v.rfid_uid)}
                    onChange={() => basculerSelection(v.rfid_uid)} />
                </td>
                <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12 }}>{v.rfid_uid}</td>
                <td style={s.td}>
                  {v.type_verre
                    ? <span style={badgeStyle('neutre')}>{v.type_verre}</span>
                    : <span style={{ color: couleurs.encreFaible, fontSize: 12 }}>—</span>}
                </td>
                <td style={s.td}>
                  {v.modele
                    ? <span style={{ fontSize: 12.5 }}>{v.modele}</span>
                    : <span style={{ color: couleurs.encreFaible, fontSize: 12 }}>—</span>}
                </td>
                <td style={s.td}><span style={{ ...s.badge, ...etatCouleur(v.etat) }}>{v.etat}</span></td>
                <td style={s.td}>{new Date(v.etat_updated_at).toLocaleString('fr-FR')}</td>
                <td style={s.td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {v.etat === 'en_circulation' ? (
                      <BoutonSm label="Remettre en stock…"
                        onClick={() => setModalAction({ portee: 'selection', uids: [v.rfid_uid] })} />
                    ) : (
                      <BoutonSupprimer onClick={() => supprimer(v.rfid_uid)} />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalAction && (
        <Modal
          titre={modalAction.portee === 'totale'
            ? `Remettre en stock TOUS les gobelets (${nbEnCirculation})`
            : `Remettre en stock ${modalAction.uids.length} gobelet(s)`}
          onClose={fermerModal}>
          <div style={s.formulaire}>
            <div style={{ padding: 12, background: couleurs.surfaceDouce, borderRadius: 8, fontSize: 12.5, color: couleurs.encreDouce, lineHeight: 1.6 }}>
              <p style={{ margin: '0 0 8px' }}><strong>Le client doit-il être remboursé ?</strong></p>
              <p style={{ margin: '0 0 6px' }}>
                <strong style={{ color: couleurs.succes }}>Oui</strong> — le gobelet a été mal associé
                (lu par erreur sur le lecteur, rattaché à la mauvaise vente). Le client a payé sa
                consigne et ne peut plus rien rendre au bac : sans remboursement ici, il perd son argent.
              </p>
              <p style={{ margin: 0 }}>
                <strong style={{ color: couleurs.danger }}>Non</strong> — le gobelet est perdu, cassé ou
                jamais rendu. La consigne est acquise, c'est le cas normal d'un gobelet non restitué.
              </p>
            </div>

            {modalAction.portee === 'totale' && (
              <p style={{ ...bandeau('danger'), margin: 0 }}>
                ⚠ Cette action porte sur <strong>{nbEnCirculation} gobelet(s)</strong> et est
                irréversible.
              </p>
            )}

            <Champ label="Motif (facultatif — apparaîtra dans l'historique)" value={motif}
              onChange={setMotif} placeholder="Ex: fin de festival, gobelets retrouvés au bar 3" />

            {modalAction.portee === 'totale' && (
              <Champ label="Mot de passe de votre compte *" value={motDePasse} type="password"
                onChange={setMotDePasse} placeholder="Le même qu'à la connexion" />
            )}

            <Bouton
              label={enCours ? 'Traitement…' : 'Remettre en stock et rembourser les clients'}
              onClick={() => executer(true)}
              disabled={enCours || (modalAction.portee === 'totale' && !motDePasse)} />
            <Bouton
              label={enCours ? 'Traitement…' : 'Remettre en stock SANS rembourser'}
              onClick={() => executer(false)} variante="danger"
              disabled={enCours || (modalAction.portee === 'totale' && !motDePasse)} />
            <Message texte={msgModal.texte} type={msgModal.type} />
          </div>
        </Modal>
      )}

      {detailLot && (
        <Modal
          titre={`Détail — ${detailLot.lignes.length} gobelet(s), ${detailLot.lot.rembourse ? 'remboursés' : 'consignes conservées'}`}
          onClose={() => setDetailLot(null)}>
          <p style={s.hint}>
            {new Date(detailLot.lot.created_at).toLocaleString('fr-FR')} — par {detailLot.lot.utilisateur_email || '—'}
            {detailLot.lot.motif ? ` — « ${detailLot.lot.motif} »` : ''}
          </p>
          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={s.th}>EPC</th>
                <th style={s.th}>Vente partenaire</th>
              </tr></thead>
              <tbody>
                {detailLot.lignes.map((l, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? couleurs.surface : couleurs.surfaceDouce }}>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12 }}>{l.rfid_uid}</td>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 11, color: couleurs.encreDouce }}>
                      {l.partenaire_transaction_id || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </Section>
  );
}
// ─── UTILISATEURS ──────────────────────────────────────────

function ModalModifierUtilisateur({ utilisateur, festivals, onClose, onSaved }) {
  const [role, setRole]           = useState(utilisateur.role);
  const [festivalId, setFestivalId] = useState(utilisateur.festival_id || '');
  const [tapisId, setTapisId]     = useState(utilisateur.tapis_identifiant || '');
  const [motDePasse, setMotDePasse] = useState('');
  const [actif, setActif]         = useState(utilisateur.actif !== false);
  const [tapis, setTapis]         = useState([]);
  const [msg, setMsg]             = useState({ texte: '', type: '' });

  useEffect(() => {
    if (festivalId) {
      api.get(`/admin/tapis/${festivalId}`)
        .then(res => setTapis(res.data))
        .catch(console.error);
    } else {
      setTapis([]);
    }
  }, [festivalId]);

  const handleFestivalChange = (fId) => { setFestivalId(fId); setTapisId(''); };

  const sauvegarder = async () => {
    if (role === 'barman' && festivalId && !tapisId) {
      setMsg({ texte: 'Un barman doit être associé à un tapis', type: 'erreur' }); return;
    }
    try {
      await api.put(`/admin/utilisateurs/${utilisateur.id}`, {
        role,
        festival_id: festivalId || null,
        tapis_id: tapisId || null,
        actif,
        ...(motDePasse ? { mot_de_passe: motDePasse } : {}),
      });
      setMsg({ texte: 'Utilisateur modifié !', type: 'succes' });
      setTimeout(() => { onSaved(); onClose(); }, 800);
    } catch (e) { setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' }); }
  };

  return (
    <Modal titre={`Modifier — ${utilisateur.email}`} onClose={onClose}>
      <div style={s.formulaire}>
        {/* Rôle, festival et tapis retirés le 02/09/2026 : il n'existe
            plus que des comptes administrateur. Les trois champs ne
            servaient qu'à rattacher un barman à son lecteur, et la
            tablette barman ne sert plus. */}
        <div style={s.champ}>
          <label style={s.label}>Nouveau mot de passe <span style={{ color: couleurs.encreFaible }}>(laisser vide = inchangé)</span></label>
          <input style={s.input} type="password" value={motDePasse}
            onChange={e => setMotDePasse(e.target.value)} placeholder="••••••••"
            autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input type="checkbox" id="actif-check" checked={actif} onChange={e => setActif(e.target.checked)} />
          <label htmlFor="actif-check" style={{ ...s.label, margin: 0 }}>Compte actif</label>
        </div>
        <Bouton label="Enregistrer les modifications" onClick={sauvegarder} />
        <Message texte={msg.texte} type={msg.type} />
      </div>
    </Modal>
  );
}

function Utilisateurs() {
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [email, setEmail]               = useState('');
  const [motDePasse, setMotDePasse]     = useState('');
  const [msg, setMsg]                   = useState({ texte: '', type: '' });

  const charger = async () => {
    try {
      setUtilisateurs((await api.get('/admin/utilisateurs')).data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { charger(); }, []);


  const creer = async () => {
    if (!email || !motDePasse) { setMsg({ texte: 'Email et mot de passe obligatoires', type: 'erreur' }); return; }
    try {
      // Ni festival ni tapis : ils ne rattachaient qu'un barman a son
      // lecteur, et il n'existe plus que des administrateurs.
      await api.post('/auth/creer-utilisateur', {
        email, mot_de_passe: motDePasse, role: 'admin',
      });
      setMsg({ texte: `Utilisateur ${email} créé !`, type: 'succes' });
      setEmail(''); setMotDePasse('');
      charger();
    } catch (e) { setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' }); }
  };

  const supprimer = async (id) => {
    try { await api.delete(`/admin/utilisateurs/${id}`); charger(); }
    catch (e) { setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' }); }
  };

  // Un rôle autre qu'admin ne peut plus être créé, mais d'anciens
  // comptes peuvent en porter un : ils s'affichent alors en neutre,
  // ce qui les rend justement repérables pour être supprimés.
  const roleCouleur = r => badgeStyle(r === 'admin' ? 'accent' : 'neutre');

  return (
    <Section titre="Utilisateurs">
      <p style={s.hint}>
        Les comptes qui donnent accès à la console. <strong>Tous sont
        administrateurs</strong> : les comptes barman ont été retirés le
        01/09/2026, la tablette barman ne servant plus — le terminal ouvre
        lui-même la page de scan.
      </p>
      <p style={{ ...s.hint, marginTop: -6 }}>
        Le dernier administrateur actif ne peut être ni supprimé, ni
        désactivé : sans lui, plus personne ne pourrait se connecter.
      </p>
      <div style={s.formulaire}>
        {/* Rôle, festival et tapis retirés en même temps : ils ne
            servaient qu'à rattacher un barman à son lecteur. */}
        <Champ label="Email *" value={email} onChange={setEmail} placeholder="responsable@festival.com" type="email" />
        <Champ label="Mot de passe *" value={motDePasse} onChange={setMotDePasse} placeholder="••••••••" type="password" />
        <Bouton label="Créer l'utilisateur" onClick={creer} />
        <Message texte={msg.texte} type={msg.type} />
      </div>

      <Liste items={utilisateurs} vide="Aucun utilisateur" renderItem={u => {
        /* Le bouton DISPARAIT sur le dernier administrateur actif.

           Le serveur refuse déjà cette suppression, et continuera de le
           faire : un bouton qu'on retire de l'écran ne ferme pas le
           point d'entrée qui est derrière. Mais proposer une action
           qu'on va refuser n'a aucun sens — au mieux on agace, au pire
           on laisse croire que le compte est supprimable et on fait
           hésiter au moment où il faudrait agir vite.

           À la place, on dit POURQUOI, et ce qu'il faut faire pour
           lever le blocage. */
        const seulAdmin = u.role === 'admin' && u.actif
          && utilisateurs.filter(x => x.role === 'admin' && x.actif).length === 1;
        return (
          <div style={s.festivalItem}>
            <div>
              <strong>{u.email}</strong>
              <span style={{ ...s.badge, ...roleCouleur(u.role) }}>{u.role}</span>
              {u.tapis_identifiant && <span style={badgeMono('succes')}>{u.tapis_identifiant}</span>}
              {!u.actif && <span style={badge('danger')}>inactif</span>}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {seulAdmin
                ? <span style={{ ...texte.aide, textAlign: 'right', maxWidth: 260 }}
                        title="Sans lui, plus personne ne pourrait se connecter à la console.">
                    Dernier administrateur — créez-en un autre pour pouvoir
                    supprimer celui-ci.
                  </span>
                : <BoutonSupprimer onClick={() => supprimer(u.id)} />}
            </div>
          </div>
        );
      }} />
    </Section>
  );
}


// ─── SAUVEGARDES ───────────────────────────────────────────
//
// Une sauvegarde est une photocopie complète de la base, prise à un
// instant précis. Elle ne sert PAS en cas de panne du serveur (le
// système se rattrape seul : sans réponse, la consigne est remboursée
// automatiquement au bout du délai) : elle sert quand une donnée a été
// détruite par une fausse manœuvre, car là, rien ne la ramène.
//
// D'où les deux origines affichées distinctement :
//   • « avant … »  → prise automatiquement juste avant une action
//     destructrice. C'est celle qu'on cherche, et elle porte son nom ;
//   • périodique   → toutes les 15 minutes, le filet du reste.
//
// Le TÉLÉCHARGEMENT compte autant que la sauvegarde elle-même : tant
// qu'une copie ne vit que sur le disque qu'elle sauvegarde, elle
// disparaît avec lui.

function octets(n) {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} Ko`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} Mo`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} Go`;
}

/* Jauge du disque du serveur.

   Elle répond à la seule question qui compte en regardant des
   sauvegardes s'accumuler : est-ce que ça va finir par remplir le
   disque ? Et si oui, est-ce à cause d'elles ?

   D'où la barre en DEUX segments : ce qu'occupe le système, et ce
   qu'occupent les sauvegardes, mis en évidence. Un seul chiffre de
   remplissage laisserait croire que les sauvegardes sont coupables
   alors qu'elles pèsent souvent un millième du disque.

   Les seuils sont posés bas et pour une raison précise : un disque
   plein n'abîme pas que les sauvegardes, il ARRÊTE PostgreSQL, qui ne
   peut plus rien écrire. C'est la panne la plus bête possible, en plein
   service, causée par le mécanisme censé protéger les données. */
function JaugeDisque({ disque }) {
  if (!disque) return null;

  const { total_octets: total, libre_octets: libre, sauvegardes_octets: sauv } = disque;
  const autre = Math.max(0, total - libre - sauv);
  const pct = (n) => (total > 0 ? (n / total) * 100 : 0);

  // Un pourcentage n'a pas de sens ici — 5 % d'un petit disque, c'est
  // peu. Ce qui compte, c'est le nombre de gigaoctets encore libres.
  const Go = 1024 * 1024 * 1024;
  const ton = libre < 1 * Go ? 'danger' : libre < 3 * Go ? 'alerte' : 'succes';
  const couleurTon = { danger: couleurs.danger, alerte: couleurs.alerte, succes: couleurs.succes }[ton];

  return (
    <div style={{ border: `1px solid ${couleurs.bord}`, borderRadius: rayon.lg,
                  padding: espace.lg, marginBottom: espace.lg }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'baseline', marginBottom: espace.sm, flexWrap: 'wrap', gap: espace.sm }}>
        <span style={texte.etiquette}>Disque du serveur</span>
        <span style={{ fontSize: 13, color: couleurTon, fontWeight: 600,
                       fontVariantNumeric: 'tabular-nums' }}>
          {octets(libre)} libres sur {octets(total)}
        </span>
      </div>

      <div style={{ display: 'flex', height: 10, borderRadius: 999,
                    overflow: 'hidden', background: couleurs.fond,
                    border: `1px solid ${couleurs.bord}` }}
           role="img"
           aria-label={`${octets(libre)} libres sur ${octets(total)}, dont ${octets(sauv)} de sauvegardes`}>
        <div style={{ width: `${pct(autre)}%`, background: couleurs.encreFaible }}
             title={`Système et base de données : ${octets(autre)}`} />
        <div style={{ width: `${pct(sauv)}%`, background: couleurs.accent }}
             title={`Sauvegardes : ${octets(sauv)}`} />
      </div>

      <div style={{ display: 'flex', gap: espace.lg, marginTop: espace.sm,
                    flexWrap: 'wrap', fontSize: 12, color: couleurs.encreDouce }}>
        <span><span style={{ ...pastille('neutre'), marginRight: 6 }} />Système et base — {octets(autre)}</span>
        <span><span style={{ ...pastille('accent'), marginRight: 6 }} />Sauvegardes — {octets(sauv)}</span>
      </div>

      {ton !== 'succes' && (
        <p style={{ ...bandeau(ton), marginTop: espace.md, marginBottom: 0 }}>
          {ton === 'danger'
            ? "Moins d'1 Go libre. Au-delà, PostgreSQL ne peut plus écrire et le système s'arrête — téléchargez puis supprimez d'anciennes sauvegardes sans attendre."
            : "Moins de 3 Go libres. Rien d'urgent, mais c'est le moment de faire du ménage dans les sauvegardes."}
        </p>
      )}
    </div>
  );
}

// La date est dans le nom : gobelets_2026-08-27_174516.sql.gz
const dateDeLArchive = (nom) => (nom || '').split('_').slice(-2)[0] || '';

/* Confirmation d'une restauration.

   C'est l'action la plus destructrice du système, et elle ne ressemble
   à aucune autre : elle ne supprime pas des lignes, elle FAIT REVENIR
   LE TEMPS EN ARRIÈRE. Tout ce qui s'est passé depuis l'archive
   disparaît — ventes, scans, remboursements.

   D'où la date à recopier plutôt que le nom complet du fichier :
   personne ne relit sérieusement « etiquette_avant-suppression_… » ;
   une date, si. Ce qu'on veut, ce n'est pas de la dactylographie,
   c'est que l'œil se pose sur la bonne ligne. */
function ModalRestauration({ sauvegarde, onClose, onLancee }) {
  const [motDePasse, setMotDePasse] = useState('');
  const [dateTapee, setDateTapee]   = useState('');
  const [enCours, setEnCours]       = useState(false);
  const [msg, setMsg]               = useState({ texte: '', type: '' });

  const dateAttendue = dateDeLArchive(sauvegarde.nom);
  const pret = motDePasse.length > 0 && dateTapee.trim() === dateAttendue && !enCours;

  const executer = async () => {
    if (!pret) return;
    setEnCours(true); setMsg({ texte: '', type: '' });
    try {
      const res = await api.post(`/admin/sauvegardes/${sauvegarde.nom}/restaurer`,
        { mot_de_passe: motDePasse, date_confirmee: dateTapee.trim() });
      onLancee(res.data);
      onClose();
    } catch (e) {
      setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' });
      setEnCours(false);
    }
  };

  return (
    <Modal titre="Restaurer cette sauvegarde" onClose={onClose}>
      <div style={s.formulaire}>
        <div style={{ ...bandeau('danger'), margin: 0 }}>
          <p style={{ margin: `0 0 ${espace.sm}px` }}>
            <strong>Le système va revenir à son état du {dateAttendue}.</strong>
          </p>
          <p style={{ margin: 0 }}>
            Tout ce qui s'est passé depuis disparaît : les ventes, les gobelets
            scannés, les remboursements. Ce n'est pas une suppression, c'est un
            retour en arrière — et il porte sur <strong>tous les festivals</strong>,
            pas seulement celui que vous administrez.
          </p>
        </div>

        <p style={{ ...texte.aide, margin: 0 }}>
          L'état actuel est sauvegardé automatiquement juste avant, sous
          l'étiquette « avant-restauration ». L'ancienne base est conservée sur
          le serveur, elle n'est pas détruite.
        </p>

        <div style={{ ...bandeau('alerte'), margin: 0 }}>
          L'application va <strong>redémarrer</strong>. Comptez une trentaine de
          secondes avant de pouvoir recharger la page. Ne le faites jamais
          pendant un service.
        </div>

        <div style={{ ...s.listeItem, marginBottom: 0 }}>
          <div style={{ fontFamily: police.mono, fontSize: 12.5 }}>{sauvegarde.nom}</div>
          <div style={{ ...texte.aide, marginTop: espace.xs }}>
            {new Date(sauvegarde.creee_at).toLocaleString('fr-FR')} — {octets(sauvegarde.taille_octets)}
          </div>
        </div>

        <Champ label={`Recopiez la date de cette sauvegarde : ${dateAttendue}`}
          value={dateTapee} onChange={setDateTapee} placeholder={dateAttendue} />
        <Champ label="Mot de passe de votre compte *" value={motDePasse} type="password"
          onChange={setMotDePasse} placeholder="Le même qu'à la connexion" />

        <Bouton label={enCours ? 'Lancement…' : 'Restaurer — revenir à cet état'}
          onClick={executer} variante="danger" disabled={!pret} />
        <Message texte={msg.texte} type={msg.type} />
      </div>
    </Modal>
  );
}

export function Sauvegardes() {
  const [liste, setListe]     = useState([]);
  const [disque, setDisque]   = useState(null);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [msg, setMsg]         = useState({ texte: '', type: '' });
  const [aRestaurer, setARestaurer] = useState(null);
  const [importEnCours, setImportEnCours] = useState(false);
  const fichierRef = React.useRef(null);

  const charger = async () => {
    try {
      const res = (await api.get('/admin/sauvegardes')).data;
      setListe(res.sauvegardes);
      setDisque(res.disque);
    }
    catch (e) { setMsg({ texte: 'Liste des sauvegardes indisponible', type: 'erreur' }); }
    setChargement(false);
  };

  /* Rafraîchissement toutes les 30 secondes : une sauvegarde tombe
     toutes les 15 minutes, et le disque n'est pas un compteur qui
     s'emballe. Plus souvent n'apprendrait rien et interrogerait le
     serveur pour rien. */
  useEffect(() => {
    charger();
    const t = setInterval(charger, 30000);
    return () => clearInterval(t);
  }, []);

  const sauvegarderMaintenant = async () => {
    if (enCours) return;
    setEnCours(true); setMsg({ texte: '', type: '' });
    try {
      const res = await api.post('/admin/sauvegardes');
      setMsg({ texte: `Sauvegarde créée : ${res.data.nom}`, type: 'succes' });
      charger();
    } catch (e) {
      setMsg({ texte: e.response?.data?.detail || 'Échec de la sauvegarde', type: 'erreur' });
    }
    setEnCours(false);
  };

  /* Le fichier n'est pas accessible par une simple adresse : il faut le
     jeton de connexion. On le récupère donc en mémoire, puis on
     déclenche l'enregistrement — sinon le navigateur demanderait le
     fichier sans être authentifié et recevrait un refus. */
  const telecharger = async (nom) => {
    setMsg({ texte: '', type: '' });
    try {
      const res = await api.get(`/admin/sauvegardes/${nom}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const lien = document.createElement('a');
      lien.href = url; lien.download = nom;
      document.body.appendChild(lien); lien.click();
      document.body.removeChild(lien);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMsg({ texte: 'Téléchargement impossible', type: 'erreur' });
    }
  };

  const supprimer = async (nom) => {
    try { await api.delete(`/admin/sauvegardes/${nom}`); charger(); }
    catch (e) { setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' }); }
  };

  /* Rapporter sur le serveur une archive gardée sur un poste.

     Le serveur n'accepte que les fichiers qu'il a lui-même produits :
     il compare la signature du fichier reçu à celles qu'il a notées en
     les fabriquant. Un fichier .sql.gz n'est pas une donnée, c'est une
     liste d'ordres exécutés avec tous les droits — sans ce contrôle,
     ce bouton accepterait n'importe quel ordre venu de n'importe où.

     L'import ne restaure rien : il replace le fichier dans la liste.
     Restaurer reste un second geste, avec ses propres protections. */
  const importer = async (evt) => {
    const fichier = evt.target.files[0];
    if (!fichier) return;
    setImportEnCours(true); setMsg({ texte: '', type: '' });
    try {
      const corps = new FormData();
      corps.append('file', fichier);
      const res = await api.post('/admin/sauvegardes/importer', corps,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg({ texte: res.data.message, type: 'succes' });
      charger();
    } catch (e) {
      setMsg({ texte: e.response?.data?.detail || "Import impossible", type: 'erreur' });
    }
    setImportEnCours(false);
    evt.target.value = '';
  };

  const restaurationLancee = (data) => {
    setMsg({ texte: data.message, type: 'succes' });
  };

  const etiquetees = liste.filter(f => f.etiquetee);
  const total = liste.reduce((n, f) => n + f.taille_octets, 0);

  return (
    <Section titre="Sauvegardes">
      <p style={s.hint}>
        Une copie complète de la base est prise automatiquement toutes les
        15 minutes, et <strong>juste avant chaque action destructrice</strong>.
        Les sauvegardes vivent sur le serveur, dans un dossier que lui seul
        peut lire. Téléchargez-en une de temps en temps : c'est la seule
        protection si le serveur lui-même venait à disparaître.
      </p>

      <JaugeDisque disque={disque} />

      <div style={{ display: 'flex', gap: espace.sm, alignItems: 'center',
                    flexWrap: 'wrap', marginBottom: espace.lg }}>
        <Bouton label={enCours ? 'Sauvegarde en cours…' : 'Sauvegarder maintenant'}
          onClick={sauvegarderMaintenant} disabled={enCours} />
        <input ref={fichierRef} type="file" accept=".gz,.sql.gz"
          onChange={importer} style={{ display: 'none' }} />
        <BoutonSm label={importEnCours ? 'Import…' : 'Importer depuis ce poste'}
          onClick={() => fichierRef.current && fichierRef.current.click()} />
        <span style={texte.aide}>
          {liste.length} archive(s) — {octets(total)}
          {etiquetees.length > 0 && ` — dont ${etiquetees.length} prise(s) avant une action`}
        </span>
      </div>

      <Message texte={msg.texte} type={msg.type} />

      {chargement && <p style={s.vide}>Chargement…</p>}
      {!chargement && liste.length === 0 && (
        <p style={s.vide}>
          Aucune sauvegarde. Si cet écran reste vide alors que le minuteur
          est censé tourner, c'est un problème à régler avant le festival.
        </p>
      )}

      <div style={s.liste}>
        {liste.map(f => (
          <div key={f.nom} style={s.listeItem}>
            <div style={s.festivalItem}>
              <div>
                <strong style={{ fontFamily: police.mono, fontSize: 13 }}>{f.nom}</strong>
                {f.etiquetee
                  ? <span style={badge('alerte')}>avant une action</span>
                  : <span style={badge('neutre')}>périodique</span>}
                <div style={{ ...texte.aide, marginTop: espace.xs }}>
                  {new Date(f.creee_at).toLocaleString('fr-FR')} — {octets(f.taille_octets)}
                  {f.libelle ? ` — ${f.libelle.replace(/-/g, ' ')}` : ''}
                </div>
              </div>
              {/* « Restaurer… » se termine par des points de suspension :
                  il n'agit pas, il ouvre une fenêtre qui réclame le mot
                  de passe et la date de l'archive. */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <BoutonSm label="Télécharger" onClick={() => telecharger(f.nom)} />
                <BoutonSm label="Restaurer…" variante="danger"
                  onClick={() => setARestaurer(f)} />
                <BoutonSupprimer onClick={() => supprimer(f.nom)} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {aRestaurer && (
        <ModalRestauration
          sauvegarde={aRestaurer}
          onClose={() => setARestaurer(null)}
          onLancee={restaurationLancee} />
      )}
    </Section>
  );
}


// ─── APP ADMIN ─────────────────────────────────────────────

/* `onFestivalChange` remonte à l'application : le festival modifié, ou
   `null` s'il vient d'être supprimé — auquel cas elle ramène d'elle-même
   à l'écran de choix, seul endroit qui ait encore un sens. */
export default function Admin({ festivalId, onFestivalChange = () => {} }) {
  const [onglet, setOnglet]                           = useState('festivals');
  // Ecran etroit : le rail de neuf onglets devient un selecteur.
  const mobile                                        = useMobile();
  const [festivalSelectionne, setFestivalSelectionne] = useState(null);

  useEffect(() => {
    if (festivalId && !festivalSelectionne) {
      api.get('/admin/festivals').then(res => {
        const f = res.data.find(f => f.id === festivalId);
        if (f) { setFestivalSelectionne(f); setOnglet('lecteurs'); }
      }).catch(console.error);
    }
  }, [festivalId]);

  // Un festival modifié ici doit se refléter partout : le bandeau du
  // haut, les autres onglets, et l'application elle-même.
  const majFestival = (f) => {
    setFestivalSelectionne(f);
    onFestivalChange(f);
  };

  // L'onglet « Bacs retour » a disparu (refonte du 2026-08-16) : bacs et
  // tapis sont le même matériel pour le collecteur, ils se vérifient
  // ensemble dans « Lecteurs ».
  const onglets = [
    // Libellés sans emoji : ils remplaçaient un mot par un pictogramme
    // ambigu (un gobelet pour « Verres », une coupe pour « Festivals »)
    // et donnaient à l'outil un registre de jouet. L'ordre suit celui
    // d'une mise en service : on déclare le festival, puis le matériel,
    // puis ce qui se vend, puis on surveille.
    // « Festival » au singulier depuis le 2026-08-27 : cet onglet ne
    // montre plus la liste, mais le seul festival administré.
    { id: 'festivals',    label: 'Festival',          disabled: !festivalSelectionne },
    { id: 'lecteurs',     label: 'Bars & lecteurs',   disabled: !festivalSelectionne },
    { id: 'bracelets',    label: 'Bracelets barman',  disabled: !festivalSelectionne },
    { id: 'commandes',    label: 'Commandes actives', disabled: !festivalSelectionne },
    { id: 'config',       label: 'Config partenaire', disabled: !festivalSelectionne },
    { id: 'produits',     label: 'Produits',          disabled: !festivalSelectionne },
    { id: 'verres',       label: 'Gobelets',          disabled: !festivalSelectionne },
    { id: 'utilisateurs', label: 'Utilisateurs' },
    // Hors festival : le journal des rejets porte sur la MACHINE,
    // pas sur un evenement. Le ranger sous un festival laisserait
    // croire qu'il ne montre que celui-la.
    { id: 'securite',     label: 'Rejets' },
    // Les sauvegardes ne sont plus ici depuis le 2026-08-28. Elles
    // portent sur la BASE ENTIÈRE, pas sur un festival : les ranger
    // dans l'administration d'un festival laissait croire le contraire.
    //
    // Et surtout, le défaut était concret : cet écran n'est atteignable
    // qu'avec un festival sélectionné. En supprimant le dernier, on
    // perdait l'accès à ses propres sauvegardes — y compris à celle
    // prise juste avant la suppression. Elles vivent désormais sur
    // l'écran de choix du festival, qui reste toujours accessible.
  ];

  return (
    <div className="jums-page" style={s.container}>
      <div className="jums-empile" style={s.header}>
        <h1 style={s.titre}>Administration</h1>
        {/* Plus de bouton « Changer » ici (retiré le 2026-08-27) : il en
            existait un second, identique, dans la barre du haut, et les
            deux n'emmenaient pas au même endroit. Un seul subsiste,
            celui de la barre, qui mène à l'écran de choix. */}
        {festivalSelectionne && (
          <div style={s.festivalActif}>
            {festivalSelectionne.nom}
            <span style={badge(festivalSelectionne.actif ? 'succes' : 'neutre')}>
              {festivalSelectionne.actif ? '● en production' : 'consultation'}
            </span>
          </div>
        )}
      </div>

      {mobile ? (
        <SelecteurSection onglets={onglets} onglet={onglet} setOnglet={setOnglet} />
      ) : (
      <div style={s.onglets}>
        {onglets.map(o => (
          <button key={o.id} style={{ ...s.onglet, ...(onglet === o.id ? s.ongletActif : {}), opacity: o.disabled ? 0.4 : 1, cursor: o.disabled ? 'not-allowed' : 'pointer' }}
            onClick={() => !o.disabled && setOnglet(o.id)}>
            {o.label}
          </button>
        ))}
      </div>
      )}

      {/* Un festival qu'on consulte ressemble en tout point à celui qui
          tourne : mêmes écrans, mêmes chiffres, mêmes boutons. Sans
          cette bande, on croit agir sur la production alors qu'on
          regarde l'an dernier — et l'inverse est pire encore. */}
      {festivalSelectionne && !festivalSelectionne.actif && (
        <p style={{ ...bandeau('alerte'), marginBottom: espace.lg }} role="status">
          Vous <strong>consultez</strong> ce festival. Il n'est pas en production :
          le collecteur ne l'alimente pas, et les ventes n'y arrivent pas.
          Pour le faire tourner, ouvrez l'écran de choix (« Changer ») et cliquez
          <strong> Rendre actif</strong>.
        </p>
      )}

      <div style={s.contenu}>
        {onglet === 'festivals' && <Festivals festival={festivalSelectionne} onFestivalChange={majFestival} />}
        {onglet === 'lecteurs'     && festivalSelectionne && <Lecteurs festival={festivalSelectionne} />}
        {onglet === 'bracelets'    && festivalSelectionne && <Bracelets festival={festivalSelectionne} />}
        {onglet === 'commandes'    && festivalSelectionne && <Commandes festival={festivalSelectionne} />}
        {onglet === 'config'       && festivalSelectionne && <ConfigPartenaire festival={festivalSelectionne} />}
        {onglet === 'produits'     && festivalSelectionne && <Produits festival={festivalSelectionne} />}
        {onglet === 'verres'       && festivalSelectionne && <Verres festival={festivalSelectionne} />}
        {onglet === 'utilisateurs' && <Utilisateurs />}
        {onglet === 'securite'     && <Securite />}
      </div>
    </div>
  );
}

/* Styles de l'administration. Tout ce qui est partagé avec les autres
   écrans vient de theme.js ; ne restent ici que les dispositions
   propres à cet écran (rail de colonnes des lecteurs, onglets, fiches).
   Les anciens noms de clés sont conservés à l'identique : le balisage
   n'a pas à connaître la refonte. */
const s = {
  container: {
    minHeight: '100vh', background: couleurs.fond,
    padding: `${52 + espace.xl}px ${espace.xl}px ${espace.xxl}px`,
    maxWidth: 1440, margin: '0 auto',
  },
  header: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    gap: espace.lg, marginBottom: espace.lg,
  },
  titre: { ...texte.titre },
  festivalActif: { ...texte.aide, margin: 0 },
  btnChanger: { ...bouton('discret', 'sm'), marginLeft: espace.sm },

  /* Onglets : un rail souligné, sans pastilles. L'onglet actif se
     signale par son trait et son encre foncée — la même grammaire que
     la barre de navigation, pour qu'on ne se demande jamais où l'on est. */
  onglets: {
    display: 'flex', gap: 2, marginBottom: espace.xl, flexWrap: 'wrap',
    borderBottom: `1px solid ${couleurs.bord}`,
  },
  onglet: {
    padding: '9px 13px', border: 'none', background: 'transparent',
    fontSize: 13.5, color: couleurs.encreDouce,
    borderBottom: '2px solid transparent', marginBottom: -1,
  },
  ongletActif: { color: couleurs.encre, fontWeight: 600, borderBottomColor: couleurs.accent },
  contenu: { display: 'flex', flexDirection: 'column', gap: espace.xl },
  section: { ...carteStyle },

  // ── Onglet Lecteurs : une colonne par bar, plus une pour les bacs ──
  // `overflowX: auto` sur le rail et une largeur FIXE par colonne : avec
  // une vingtaine de lecteurs sur cinq bars, on fait défiler
  // horizontalement plutôt que de compresser les colonnes jusqu'à les
  // rendre illisibles.
  bandeauOk:     { ...bandeau('succes'), marginBottom: espace.md },
  bandeauAlerte: { ...bandeau('alerte'), marginBottom: espace.md },
  bandeauNeutre: { ...bandeau('neutre'), marginBottom: espace.md },
  colonnes: {
    display: 'flex', gap: espace.md, overflowX: 'auto',
    alignItems: 'flex-start', paddingBottom: espace.sm,
  },
  // Deux colonnes d'égale importance (Produits, rev28) — et non le
  // défilement horizontal de `colonnes`, taillé pour N bars. En dessous
  // de ~600 px elles se replient l'une sous l'autre plutôt que de
  // s'écraser.
  deuxColonnes: {
    display: 'grid', gap: espace.md,
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    alignItems: 'start',
  },
  colonneLarge: {
    background: couleurs.fond, border: `1px solid ${couleurs.bord}`,
    borderRadius: rayon.lg, padding: espace.md, minWidth: 0,
  },
  colonne: {
    flex: '0 0 268px', background: couleurs.fond,
    border: `1px solid ${couleurs.bord}`, borderRadius: rayon.lg, padding: espace.md,
  },
  // Les bacs ne sont plus vert pâle : ce n'était pas un état, juste une
  // couleur. Un liseré d'accent suffit à dire « cette colonne est d'une
  // autre nature ».
  //
  // Le liseré passe à GAUCHE le 2026-09-04, et sur toute la hauteur : en
  // haut, il se confondait avec la bordure des colonnes de bars dès
  // qu'on faisait défiler. À gauche, il longe le filet de séparation et
  // marque le début du camp des bacs.
  colonneBac: {
    flex: '0 0 268px', background: couleurs.fond,
    border: `1px solid ${couleurs.bord}`, borderLeft: `3px solid ${couleurs.accent}`,
    borderRadius: rayon.lg, padding: espace.md,
  },
  /* ── Les deux camps de l'onglet Lecteurs (2026-09-04) ────────────
     Un camp = un titre, puis son rang de colonnes. Le titre ne défile
     PAS avec les colonnes qu'il coiffe : il est posé au-dessus du rang,
     à l'intérieur du camp, donc il suit son camp et reste lisible même
     avec dix bars. */
  groupeLecteurs: { display: 'flex', flexDirection: 'column', gap: espace.sm },
  groupeEntete: {
    display: 'flex', alignItems: 'baseline', gap: espace.sm,
    padding: `0 ${espace.xs}px`,
  },
  groupeTitre: {
    fontSize: 12, fontWeight: 600, letterSpacing: '0.04em',
    textTransform: 'uppercase', color: couleurs.encreDouce, whiteSpace: 'nowrap',
  },
  groupeCompte: { fontSize: 11.5, color: couleurs.encreFaible, whiteSpace: 'nowrap' },
  groupeRang: { display: 'flex', gap: espace.md, alignItems: 'flex-start' },
  // Le filet qui sépare les deux camps. `alignSelf: stretch` pour qu'il
  // aille du haut des titres au bas de la plus longue colonne : un trait
  // à mi-hauteur ne sépare rien, il décore.
  separateurLecteurs: {
    alignSelf: 'stretch', flex: '0 0 1px', width: 1,
    background: couleurs.bordFort, margin: `0 ${espace.sm}px`,
  },
  colonneEntete: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: espace.sm, marginBottom: espace.md,
  },
  colonneTitre: {
    fontWeight: 600, fontSize: 13.5, color: couleurs.encre,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  colonneSous: { fontSize: 11.5, color: couleurs.encreDouce, marginTop: 2 },
  colonneVide: { fontSize: 12, color: couleurs.encreFaible, textAlign: 'center', padding: '12px 0', margin: 0 },
  colonneAjout: {
    width: '100%', padding: '7px', marginTop: espace.xs,
    background: couleurs.surface, border: `1px dashed ${couleurs.bordFort}`,
    borderRadius: rayon.md, color: couleurs.encreDouce, fontSize: 12.5, cursor: 'pointer',
  },
  carteLecteur: {
    background: couleurs.surface, border: `1px solid ${couleurs.bord}`,
    borderRadius: rayon.lg, padding: espace.md, marginBottom: espace.sm,
  },
  carteEntete: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: espace.xs, marginBottom: espace.xs,
  },
  carteId: {
    fontFamily: police.mono, fontSize: 12.5, fontWeight: 600, color: couleurs.encre,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  carteNom: { fontSize: 11.5, color: couleurs.encreDouce, marginBottom: espace.sm },
  carteLigne: { display: 'flex', justifyContent: 'space-between', gap: espace.sm, fontSize: 11.5, marginTop: 3 },
  carteLabel: { color: couleurs.encreFaible, flex: 'none' },
  carteValeur: {
    fontFamily: police.mono, fontSize: 11.5, color: couleurs.encre,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  carteManque: { color: couleurs.danger, fontWeight: 600 },
  carteActions: { display: 'flex', gap: espace.xs, marginTop: espace.md },
  // « prêt » / « à régler » : un point de couleur suivi du mot, plutôt
  // qu'une pastille pleine. Le point porte l'information de loin, le
  // mot la confirme de près.
  pastilleOk: {
    flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 11, fontWeight: 600, color: couleurs.succes,
  },
  pastilleAlerte: {
    flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 11, fontWeight: 600, color: couleurs.danger,
  },
  // « muet » (rev33) : orange et non rouge. Le rouge est réservé à ce
  // qui n'a jamais été configuré — une faute de préparation, à corriger
  // avant l'ouverture. Un lecteur muet, lui, est correctement réglé :
  // c'est un câble, une alimentation ou un switch, et ça se répare en
  // trente secondes sans toucher à la console.
  pastilleMuet: {
    flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 11, fontWeight: 600, color: couleurs.alerte,
  },
  sectionTitre: { ...texte.section, fontSize: 16 },
  formulaire: { display: 'flex', flexDirection: 'column', gap: espace.md, marginBottom: espace.xl, maxWidth: 520 },
  champ: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 12.5, color: couleurs.encreDouce, fontWeight: 500,
           display: 'inline-flex', alignItems: 'center' },
  input: { ...champStyle },
  bouton: { ...bouton('primaire') },
  boutonSm: { ...bouton('discret', 'sm') },
  // Le « ＋ » plein écran devient un bouton carré discret : c'est une
  // action d'ajout, pas le point focal de la page.
  btnPlus: {
    width: 30, height: 30, borderRadius: rayon.md, background: couleurs.surface,
    color: couleurs.encre, border: `1px solid ${couleurs.bordFort}`,
    fontSize: 17, cursor: 'pointer', lineHeight: 1, flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  },
  message: { ...bandeau('neutre'), margin: 0 },
  liste: { display: 'flex', flexDirection: 'column', gap: espace.sm },
  listeItem: {
    padding: '11px 14px', background: couleurs.surface,
    borderRadius: rayon.lg, border: `1px solid ${couleurs.bord}`,
  },
  vide: { ...texte.aide, fontStyle: 'normal', padding: `${espace.md}px 0`, margin: 0 },
  badge: { ...badgeStyle('neutre'), marginLeft: espace.sm },
  idLigne: { display: 'flex', alignItems: 'center', gap: espace.sm, marginTop: espace.xs },
  idTexte: { fontSize: 11.5, fontFamily: police.mono, color: couleurs.encreFaible },
  idCopier: {
    fontSize: 11, padding: '2px 7px', borderRadius: rayon.sm,
    border: `1px solid ${couleurs.bord}`, background: couleurs.surface,
    color: couleurs.encreDouce, cursor: 'pointer',
  },
  festivalItem: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: espace.sm,
  },
  festivalDates: { fontSize: 12.5, color: couleurs.encreDouce, marginLeft: espace.sm },
  hint: { ...texte.aide, margin: `0 0 ${espace.md}px`, maxWidth: '78ch' },
  sousTitre: { ...texte.etiquette, display: 'block', margin: `${espace.xl}px 0 ${espace.sm}px` },
  iconInfo: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 15, height: 15, marginLeft: 5, borderRadius: rayon.rond,
    border: `1px solid ${couleurs.bordFort}`, background: couleurs.surface,
    color: couleurs.encreDouce, fontSize: 10, fontWeight: 700, lineHeight: 1,
    cursor: 'pointer', padding: 0, verticalAlign: 'middle',
  },
  // L'infobulle reste sombre : c'est un calque au-dessus de la page,
  // l'inversion de contraste dit qu'il flotte, sans ombre portée.
  bulleInfo: {
    position: 'absolute', top: 21, left: 0, zIndex: 10, width: 300,
    background: couleurs.bulle, color: couleurs.surBulle, fontSize: 12.5, fontWeight: 400,
    lineHeight: 1.55, borderRadius: rayon.lg, padding: '12px 14px',
  },
  bulleInfoFermer: {
    position: 'absolute', top: 7, right: 7, width: 17, height: 17,
    borderRadius: rayon.sm, background: 'rgba(255,255,255,0.12)', border: 'none',
    color: couleurs.surBulle, fontSize: 10, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
  },
  th: { ...tableau.th },
  td: { ...tableau.td },
  overlay: {
    position: 'fixed', inset: 0, background: couleurs.voile,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 3000, padding: espace.lg,
  },
  modal: {
    background: couleurs.surface, borderRadius: rayon.xl, padding: 28,
    width: 480, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto',
    border: `1px solid ${couleurs.bord}`,
  },
};
